import CalendarMonth from '@mui/icons-material/CalendarMonth'
import Person from '@mui/icons-material/Person'
import ExitToApp from '@mui/icons-material/ExitToApp'
import AccessTime from '@mui/icons-material/AccessTime'
import People from '@mui/icons-material/People'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import LocalHospital from '@mui/icons-material/LocalHospital'
import LocationOn from '@mui/icons-material/LocationOn'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Settings from '@mui/icons-material/Settings'
import EventNote from '@mui/icons-material/EventNote'
import BarChart from '@mui/icons-material/BarChart'
import Dashboard from '@mui/icons-material/Dashboard'
import ViewAgenda from '@mui/icons-material/ViewAgenda'
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Typography,
  Divider,
  Tooltip,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material'
import { styled as muiStyled } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useState, useMemo } from 'react'
import { toast } from 'react-toastify'
import { useNavigate, useLocation } from 'react-router-dom'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import {
  MOMENT_FORMATS,
  SESSION_PLATFORM_ONLINE,
  SESSION_PLATFORM_OFFLINE,
  allAvailableEventTypes,
} from 'src/consumer/constants'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  logoutHandlerMentor,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import PortalSwitcherButton from '../MentorHeader/PortalSwitcherButton'
import { AvailabilityPageContainer } from '../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { HeaderContainer, HeaderGreeting } from '../MentorHeader/MentorHeaderStyles'
import { ClinicChoosingContainer } from './MentorClinicBookingStyles'
import ClinicLocationStatsDrawer from './component/ClinicLocationStatsDrawer'
import MentorClinicAssociationsListView from './component/MentorClinicAssociationsListView'
import MentorAssociationClinicSelectionBox from './component/MentorAssociationClinicSelectionBox'
import ClinicReportsWithVisualizations from './component/ClinicReportsWithVisualizations'
import { getMPDataWithoutParams } from 'src/mentor/services'
import MentorPastEventsListing from '../MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { checkIntoRoom } from 'src/clinic/components/CheckIn/services'
import {
  broadcastCheckInUpdate,
  updateCheckInWithValidation,
} from 'src/clinic/components/CheckIn/checkInSyncService'
import {
  getEventTypesFromCheckInOption,
  getEventTypesFromRoomData,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import { getTeacherChamberRoomScreensStatus } from 'src/consumer/services/featureServices'
import {
  buildBrandsFromLocations,
  buildRosterPresenceIntervalsByRoomId,
  EMPTY_ROSTER_ASSIGNMENT_INDEX,
  getRosterAssignmentDateTime,
  getRosterJoinedStatusForAssignmentGroup,
  isCheckedIntoRosterRoom,
} from './rosterAssignments'

// Styled Tabs matching mentor portal theme - responsive
const MentorTabs = muiStyled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  backgroundColor: 'rgba(116, 80, 170, 0.04)',
  borderRadius: '8px',
  padding: isMobile ? '2px' : '4px',
  minHeight: 'auto',
  width: '100%',
  overflowX: isMobile ? 'auto' : 'visible',
  '& .MuiTabs-indicator': {
    backgroundColor: 'rgba(116, 80, 170, 0.95)',
    height: '3px',
    borderRadius: '3px',
  },
  '& .MuiTabs-flexContainer': {
    gap: isMobile ? '2px' : '4px',
    overflowX: isMobile ? 'auto' : 'visible',
    display: 'flex',
    width: '100%',
    justifyContent: isMobile ? 'flex-start' : 'space-between',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    order: 3,
  },
}))

const MentorTab = muiStyled(Tab, {
  shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'iconOnly',
})(({ theme, isMobile, iconOnly }) => ({
    textTransform: 'none',
    minWidth: iconOnly ? '48px' : isMobile ? '120px' : 'auto',
    flex: iconOnly ? '0 0 auto' : isMobile ? '0 1 auto' : '1 1 0',
    maxWidth: iconOnly ? '48px' : 'none',
    fontWeight: theme.typography.fontWeightRegular,
    padding: iconOnly ? '8px' : isMobile ? '8px 12px' : '10px 16px',
    color: 'rgba(71, 85, 105, 0.85)',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontSize: isMobile ? '12px' : '14px',
    fontFamily: "'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    whiteSpace: 'nowrap',
    display: 'flex !important',
    visibility: 'visible !important',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    '& .MuiTab-iconWrapper': {
      marginRight: iconOnly ? 0 : '8px',
      marginBottom: iconOnly ? 0 : '0px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Only apply icon-only styles when iconOnly is explicitly true
    ...(iconOnly === true && {
      // Hide label text spans, but keep icon wrapper visible
      '& > span:not(.MuiTab-iconWrapper)': {
        display: 'none !important',
      },
      '& .MuiTab-iconWrapper': {
        display: 'flex !important',
        marginRight: '0 !important',
      },
    }),
    '&:hover': {
      color: 'rgba(116, 80, 170, 0.95)',
      backgroundColor: 'rgba(116, 80, 170, 0.08)',
      opacity: 1,
    },
    '&.Mui-selected': {
      color: 'rgba(116, 80, 170, 0.95)',
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(116, 80, 170, 0.12)',
    },
  }),
)

const OPEN_IDS = {
  brand: 'brand',
  location: 'location',
  profile: 'profile',
}

const VIEW_MODES = {
  list: 'list',
  cards: 'cards',
}

const IN_PERSON_CHECKIN_METHOD = 'check-in-method-in-person'
const REMOTE_CHECKIN_METHOD = 'check-in-method-remote'

const ROSTER_EVENT_TYPE_LABELS = {
  consultation: 'Consultation',
  'therapy-session': 'Therapy',
  assessment: 'Therapy',
  screening: 'Screening',
}

const ROSTER_ROOM_SERVICE_CONFIG = [
  {
    key: 'consultation',
    roleFlag: 'isDoctor',
    eventTypes: [allAvailableEventTypes.consultation],
  },
  {
    key: 'therapy',
    roleFlag: 'isTherapist',
    eventTypes: [allAvailableEventTypes.therapySession, allAvailableEventTypes.assessment],
  },
  {
    key: 'screening',
    roleFlag: 'isScreener',
    eventTypes: [allAvailableEventTypes.screening],
  },
]

const ROSTER_ROOM_SERVICE_CONFIG_MAP = ROSTER_ROOM_SERVICE_CONFIG.reduce(
  (accumulator, serviceConfig) => {
    accumulator[serviceConfig.key] = serviceConfig
    return accumulator
  },
  {},
)

function getRosterCheckInServiceKey(rosterEventType, roleFlags = {}) {
  const normalizedEventType =
    typeof rosterEventType === 'string'
      ? rosterEventType
      : rosterEventType?.type || rosterEventType?.id || ''

  const matchedServiceConfig = ROSTER_ROOM_SERVICE_CONFIG.find((serviceConfig) =>
    serviceConfig.eventTypes.includes(normalizedEventType),
  )

  if (matchedServiceConfig?.key) {
    return matchedServiceConfig.key
  }

  if (roleFlags?.isScreener) return 'screening'
  if (roleFlags?.isTherapist) return 'therapy'
  if (roleFlags?.isDoctor) return 'consultation'

  return null
}

function getRosterEventTypeIdentity(eventType) {
  return (
    eventType?.uid ||
    eventType?.id ||
    `${eventType?.type || 'unknown'}-${eventType?.contextId || 'global'}-${
      eventType?.title || eventType?.name || 'event'
    }`
  )
}

function dedupeRosterEventTypes(eventTypes = []) {
  const seenEventTypeIds = new Set()

  return eventTypes.filter((eventType) => {
    if (!eventType) return false

    const identity = getRosterEventTypeIdentity(eventType)
    if (seenEventTypeIds.has(identity)) return false

    seenEventTypeIds.add(identity)
    return true
  })
}

function getEnabledRosterClinicEventTypes(mentorEventTypes = [], clinicId) {
  return mentorEventTypes.filter(
    (eventType) =>
      eventType?.enabled !== false &&
      (!clinicId || !eventType?.contextId || eventType?.contextId === clinicId),
  )
}

function getRosterServiceKeysFromEventTypes(eventTypes = []) {
  const roomServiceKeys = new Set()

  eventTypes.forEach((eventType) => {
    const eventTypeValue = typeof eventType === 'string' ? eventType : eventType?.type

    ROSTER_ROOM_SERVICE_CONFIG.forEach((serviceConfig) => {
      if (serviceConfig.eventTypes.includes(eventTypeValue)) {
        roomServiceKeys.add(serviceConfig.key)
      }
    })
  })

  return [...roomServiceKeys]
}

function filterRosterEventTypesForService(eventTypes = [], serviceKey) {
  const allowedEventTypes = ROSTER_ROOM_SERVICE_CONFIG_MAP?.[serviceKey]?.eventTypes || []

  return dedupeRosterEventTypes(
    eventTypes.filter((eventType) => allowedEventTypes.includes(eventType?.type)),
  )
}

function getRosterCheckInOptionSearchText(checkInOption) {
  return [checkInOption?.title, checkInOption?.name, checkInOption?.id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function getRosterServiceKeysFromOptionText(checkInOption) {
  const optionSearchText = getRosterCheckInOptionSearchText(checkInOption)
  const roomServiceKeys = new Set()

  if (optionSearchText.includes('consult')) {
    roomServiceKeys.add('consultation')
  }
  if (optionSearchText.includes('therapy') || optionSearchText.includes('assessment')) {
    roomServiceKeys.add('therapy')
  }
  if (optionSearchText.includes('screen')) {
    roomServiceKeys.add('screening')
  }

  return [...roomServiceKeys]
}

function getRosterEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId) {
  const matchedEventTypes = getEventTypesFromCheckInOption(checkInOption, mentorEventTypes, clinicId)
  const directEventTypes = Array.isArray(checkInOption?.eventTypes)
    ? checkInOption.eventTypes.filter(
        (eventType) =>
          eventType?.enabled !== false &&
          (!clinicId || !eventType?.contextId || eventType?.contextId === clinicId),
      )
    : []

  return dedupeRosterEventTypes([...matchedEventTypes, ...directEventTypes])
}

function getRosterRoomEventTypes(roomData, clinicId, mentorEventTypes = []) {
  const helperEventTypes = getEventTypesFromRoomData(
    roomData?.checkInEventOptions,
    mentorEventTypes,
    clinicId,
  )
  const optionEventTypes = (roomData?.checkInEventOptions || []).flatMap((checkInOption) =>
    getRosterEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
  )

  return dedupeRosterEventTypes([...helperEventTypes, ...optionEventTypes])
}

function getRosterServiceKeysFromCheckInOption(checkInOption, mentorEventTypes, clinicId) {
  return [
    ...new Set([
      ...getRosterServiceKeysFromEventTypes(
        getRosterEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
      ),
      ...getRosterServiceKeysFromOptionText(checkInOption),
    ]),
  ]
}

function resolveRosterCheckInOptionForService(roomData, serviceKey, mentorEventTypes = [], clinicId) {
  const roomOptions = roomData?.checkInEventOptions || []

  return roomOptions
    .map((checkInOption, index) => {
      const roomServiceKeys = getRosterServiceKeysFromCheckInOption(
        checkInOption,
        mentorEventTypes,
        clinicId,
      )

      if (!roomServiceKeys.includes(serviceKey)) {
        return null
      }

      const matchedEventTypes = filterRosterEventTypesForService(
        getRosterEventTypesFromCheckInOptionData(checkInOption, mentorEventTypes, clinicId),
        serviceKey,
      )
      const optionSearchText = getRosterCheckInOptionSearchText(checkInOption)
      const hasSingleService = roomServiceKeys.length === 1
      const hasPreferredKeyword =
        serviceKey === 'consultation'
          ? optionSearchText.includes('consult')
          : serviceKey === 'therapy'
          ? optionSearchText.includes('therapy') || optionSearchText.includes('assessment')
          : optionSearchText.includes('screen')

      return {
        checkInOption,
        matchedEventTypes,
        score:
          (hasSingleService ? 0 : 10) +
          (matchedEventTypes.length > 0 ? 0 : 3) +
          (hasPreferredKeyword ? 0 : 1) +
          index / 100,
      }
    })
    .filter(Boolean)
    .sort((optionA, optionB) => optionA.score - optionB.score)[0]
}

function resolvePrimaryRosterServiceEntry({
  roomData,
  clinicId,
  mentorEventTypes = [],
  rosterEventType,
  roleFlags = {},
}) {
  if (!roomData) {
    return null
  }

  const serviceKey = getRosterCheckInServiceKey(rosterEventType, roleFlags)
  if (!serviceKey) {
    return null
  }

  const clinicScopedMentorEventTypes = getEnabledRosterClinicEventTypes(mentorEventTypes, clinicId)
  const roomEventTypes = getRosterRoomEventTypes(roomData, clinicId, clinicScopedMentorEventTypes)
  const optionMatch = resolveRosterCheckInOptionForService(
    roomData,
    serviceKey,
    clinicScopedMentorEventTypes,
    clinicId,
  )
  const mentorServiceEventTypes = filterRosterEventTypesForService(
    clinicScopedMentorEventTypes,
    serviceKey,
  )
  const roomServiceEventTypes = filterRosterEventTypesForService(roomEventTypes, serviceKey)

  return {
    key: serviceKey,
    checkInOption: optionMatch?.checkInOption || null,
    roomEventTypes:
      optionMatch?.matchedEventTypes?.length > 0
        ? optionMatch.matchedEventTypes
        : roomServiceEventTypes.length > 0
        ? roomServiceEventTypes
        : mentorServiceEventTypes,
  }
}

// Styled Clinic Brand Card - Modernized
// Note: isSingle is filtered out to prevent React DOM warning
const ClinicBrandCard = muiStyled(Card, {
  shouldForwardProp: (prop) => prop !== 'isSingle',
})(({ isSingle }) => ({
  width: '100%',
  maxWidth: isSingle ? '1200px' : '400px',
  margin: '0 auto',
  borderRadius: '20px',
  boxShadow: '0 2px 12px rgba(116, 80, 170, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1.5px solid rgba(116, 80, 170, 0.12)',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, rgba(116, 80, 170, 0.6) 0%, rgba(116, 80, 170, 0.3) 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    boxShadow: '0 8px 32px rgba(116, 80, 170, 0.2)',
    transform: 'translateY(-6px)',
    borderColor: 'rgba(116, 80, 170, 0.35)',
    '&::before': {
      opacity: 1,
    },
  },
}))

function getMyClinicAssociationsTimeRange() {
  return {
    startTime: dayjs().startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
    endTime: dayjs().endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
  }
}

function buildMyClinicAssociationsApiUrl(pathname) {
  const { startTime, endTime } = getMyClinicAssociationsTimeRange()
  return `${pathname}?startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}`
}

function buildMyClinicAssociationsUsageLogsApiUrl({
  fromDate,
  toDate,
  chamberRoomIds = [],
  logEventIds = [],
}) {
  const params = new URLSearchParams()

  if (fromDate) {
    params.set('fromDate', fromDate)
  }
  if (toDate) {
    params.set('toDate', toDate)
  }
  if (Array.isArray(chamberRoomIds) && chamberRoomIds.length > 0) {
    params.set('chamberRoomIds', chamberRoomIds.filter(Boolean).join(','))
  }
  if (Array.isArray(logEventIds) && logEventIds.length > 0) {
    params.set('logEventIds', logEventIds.filter(Boolean).join(','))
  }

  return `/api/secure/teacher/my-clinic-associations/usage-logs?${params.toString()}`
}

function buildWaitingPatientsDataFromBrands(brands = []) {
  return brands.reduce((accumulator, brand) => {
    ;(brand?.locations || []).forEach((location) => {
      const key = `${brand?.id}-${location?.id}`
      accumulator[key] = {
        count: location?.bookingSummary?.waitingCount || 0,
        totalBookings: location?.bookingSummary?.totalBookings || 0,
        earliestTime: location?.bookingSummary?.earliestTime || null,
        events: [],
      }
    })
    return accumulator
  }, {})
}

function flattenClinicLocations(brands = []) {
  return brands.flatMap((brand) => brand?.locations || [])
}

function getRosterCheckInMethodOptions(roomData) {
  const methodOptions = Array.isArray(roomData?.checkInMethodOptions)
    ? roomData.checkInMethodOptions
    : []

  if (methodOptions.length > 0) {
    return methodOptions
  }

  return [
    {
      id: IN_PERSON_CHECKIN_METHOD,
      title: 'In Person',
      enabled: true,
    },
  ]
}

function isRosterCheckInMethodEnabled(methodOption) {
  return methodOption?.enabled !== false
}

function resolveRosterCheckInMethod(roomData, roleFlags = {}, checkedInMethod = null) {
  const methodOptions = getRosterCheckInMethodOptions(roomData)
  const enabledMethodOptions = methodOptions.filter(isRosterCheckInMethodEnabled)
  const candidateMethodOptions = enabledMethodOptions.length > 0 ? enabledMethodOptions : methodOptions

  if (
    checkedInMethod &&
    candidateMethodOptions.some((methodOption) => methodOption?.id === checkedInMethod)
  ) {
    return checkedInMethod
  }

  const preferRemoteByDefault = roleFlags?.isTherapist || roleFlags?.isScreener
  if (
    preferRemoteByDefault &&
    candidateMethodOptions.some((methodOption) => methodOption?.id === REMOTE_CHECKIN_METHOD)
  ) {
    return REMOTE_CHECKIN_METHOD
  }

  if (
    candidateMethodOptions.some((methodOption) => methodOption?.id === IN_PERSON_CHECKIN_METHOD)
  ) {
    return IN_PERSON_CHECKIN_METHOD
  }

  return candidateMethodOptions?.[0]?.id || IN_PERSON_CHECKIN_METHOD
}

function getRosterCheckInMethodLabel(methodId) {
  return methodId === REMOTE_CHECKIN_METHOD ? 'Remotely' : 'In Person'
}

function getRosterEventTypeLabel(eventType, roleFlags = {}) {
  const normalizedEventType =
    typeof eventType === 'string' ? eventType : eventType?.type || eventType?.id || ''

  if (ROSTER_EVENT_TYPE_LABELS[normalizedEventType]) {
    return ROSTER_EVENT_TYPE_LABELS[normalizedEventType]
  }

  if (roleFlags?.isScreener) return 'Screening'
  if (roleFlags?.isTherapist) return 'Therapy'
  if (roleFlags?.isDoctor) return 'Consultation'

  return 'Session'
}

function formatRosterDuration(seconds) {
  const totalSeconds = Math.max(0, Math.floor(seconds || 0))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const remainingSeconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(remainingSeconds).padStart(
      2,
      '0',
    )}s`
  }

  if (minutes > 0) {
    return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`
  }

  return `${remainingSeconds}s`
}

function getRosterTimingMeta(assignmentGroup, now, isMatched) {
  const startTime = getRosterAssignmentDateTime(assignmentGroup?.primaryAssignment, 'start')

  if (!startTime) {
    return null
  }

  if (assignmentGroup?.status === 'upcoming') {
    return {
      tone: 'upcoming',
      label: `Starts in ${formatRosterDuration(startTime.diff(now, 'second'))}`,
      helperText: `Scheduled to begin at ${startTime.format('h:mm A')}`,
    }
  }

  const secondsSinceStart = now.diff(startTime, 'second')

  if (assignmentGroup?.status === 'current') {
    if (isMatched) {
      return {
        tone: 'matched',
        label: `Live now · ${formatRosterDuration(secondsSinceStart)} since start`,
        helperText: `Started at ${startTime.format('h:mm A')}`,
      }
    }

    return {
      tone: 'late',
      label: `Late by ${formatRosterDuration(secondsSinceStart)}`,
      helperText: `Slot started at ${startTime.format('h:mm A')}`,
    }
  }

  return {
    tone: isMatched ? 'completed' : 'late',
    label: isMatched
      ? `Started ${formatRosterDuration(secondsSinceStart)} ago`
      : `Missed by ${formatRosterDuration(secondsSinceStart)}`,
    helperText: `Scheduled start was ${startTime.format('h:mm A')}`,
  }
}

function mergeLiveStatusIntoBrands(brands = [], liveStatusData = {}) {
  const bookingSummaryByLocationId = liveStatusData?.bookingSummaryByLocationId || {}
  const roomStatusByRoomId = liveStatusData?.roomStatusByRoomId || {}
  const auditStatusByRoomId = liveStatusData?.auditStatusByRoomId || {}

  return brands.map((brand) => ({
    ...brand,
    locations: (brand?.locations || []).map((location) => ({
      ...location,
      bookingSummary: bookingSummaryByLocationId?.[location?.id]
        ? {
            ...(location?.bookingSummary || {}),
            ...bookingSummaryByLocationId[location.id],
          }
        : location?.bookingSummary,
      rooms: (location?.rooms || []).map((room) => ({
        ...room,
        ...(roomStatusByRoomId?.[room?.id] || {}),
        ...(auditStatusByRoomId?.[room?.id] || {}),
      })),
    })),
  }))
}

export default function MentorClinicAssociationsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const practitionerRoleFlags = useMemo(() => getAllRolesInBoolean(), [])
  const [showClinicStats, setShowClinicStats] = useState(false)
  const [selectedClinicLocationForClinicStats, setSelectedClinicLocationForClinicStats] =
    useState(null)
  const [mentorClinicOverviewBrands, setMentorClinicOverviewBrands] = useState([])
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [overviewLoaded, setOverviewLoaded] = useState(false)
  const [useLegacyOverviewFallback, setUseLegacyOverviewFallback] = useState(false)
  const [checkedInDetails, setCheckedInDetails] = useState(null)
  const [rosterBannerNow, setRosterBannerNow] = useState(() => dayjs())
  const [rosterUsageLogs, setRosterUsageLogs] = useState([])
  const [primaryRosterActionLoading, setPrimaryRosterActionLoading] = useState(false)
  const [chamberRoomScreenStatusesList, setChamberRoomScreenStatusesList] = useState([])
  // Sub-tabs for manage-bookings page
  const [manageBookingsSubTab, setManageBookingsSubTab] = useState(0) // 0: All Booked, 1: In Clinic, 2: Video Call, 3: Not Confirmed

  // This page is already dense and doesn't need the global DocSide helper mounted.
  const { setShowDocSide } = useContext(DocSideContext)
  
  useEffect(() => {
    setShowDocSide(false)

    return () => {
      setShowDocSide(true)
    }
  }, [setShowDocSide])

  // Check for checked-in status
  useEffect(() => {
    const checkCheckedInStatus = () => {
      try {
        const checkedInData = localStorage.getItem('checkedInDetails')
        if (checkedInData) {
          const parsed = JSON.parse(checkedInData)
          setCheckedInDetails(parsed)
        } else {
          setCheckedInDetails(null)
        }
      } catch (error) {
        console.error('Error parsing checked-in details:', error)
        setCheckedInDetails(null)
      }
    }

    // Check initially
    checkCheckedInStatus()

    // Listen for storage changes
    const handleStorageChange = () => {
      checkCheckedInStatus()
    }

    window.addEventListener('storage', handleStorageChange)

    // Also check periodically (in case of same-tab updates)
    // Increased interval to 5 seconds to reduce unnecessary re-renders that can close dialogs
    const interval = setInterval(checkCheckedInStatus, 5000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('mentorClinicAssociationsViewMode')
    if (savedViewMode === VIEW_MODES.list || savedViewMode === VIEW_MODES.cards) {
      return savedViewMode
    }

    return VIEW_MODES.list
  })

  const {
    mentorProfileData,
    mentorClinicAssociationData: mentorClinicAssociationDataFromContext,
    mentorClinicChambersData,
    rosterAssignmentsLoading = false,
    rosterAssignmentIndex = EMPTY_ROSTER_ASSIGNMENT_INDEX,
    refreshTodayRosterAssignments,
  } = useContext(MentorProfileContext)
  const mentorClinicAssociationData = useMemo(
    () =>
      mentorClinicOverviewBrands?.length
        ? flattenClinicLocations(mentorClinicOverviewBrands)
        : mentorClinicAssociationDataFromContext || [],
    [mentorClinicAssociationDataFromContext, mentorClinicOverviewBrands],
  )
  const primaryRosterGroup = rosterAssignmentIndex?.summary?.primaryGroup || null
  const rosteredRoomIds = useMemo(
    () =>
      [
        ...new Set(
          (rosterAssignmentIndex?.orderedRoomGroups || [])
            .map((assignmentGroup) => assignmentGroup?.chamberRoomId)
            .filter(Boolean),
        ),
      ],
    [rosterAssignmentIndex?.orderedRoomGroups],
  )
  const primaryRosterContext = useMemo(() => {
    if (!primaryRosterGroup?.clinicLocationId) {
      return {
        locationData: null,
        roomData: null,
      }
    }

    const locationData =
      mentorClinicAssociationData.find(
        (clinicLocation) =>
          String(clinicLocation?.id || '') === String(primaryRosterGroup?.clinicLocationId || ''),
      ) || null
    const roomData =
      locationData?.rooms?.find(
        (roomItem) =>
          String(roomItem?.id || '') === String(primaryRosterGroup?.chamberRoomId || ''),
      ) || null

    return {
      locationData,
      roomData,
    }
  }, [
    mentorClinicAssociationData,
    primaryRosterGroup?.chamberRoomId,
    primaryRosterGroup?.clinicLocationId,
  ])
  const waitingPatientsData = useMemo(
    () => buildWaitingPatientsDataFromBrands(mentorClinicOverviewBrands),
    [mentorClinicOverviewBrands],
  )
  const rosterPresenceIntervalsByRoomId = useMemo(
    () => buildRosterPresenceIntervalsByRoomId(rosterUsageLogs, rosterBannerNow),
    [rosterUsageLogs, rosterBannerNow],
  )
  const completedRosterSummary = useMemo(() => {
    const roomGroups = rosterAssignmentIndex?.orderedRoomGroups || []
    if (roomGroups.length === 0) {
      return {
        totalRooms: 0,
        joinedRooms: 0,
        partialRooms: 0,
        missedRooms: 0,
        roomSummaries: [],
      }
    }

    const roomSummaries = roomGroups.map((assignmentGroup) => {
      const participation = getRosterJoinedStatusForAssignmentGroup(
        assignmentGroup,
        rosterPresenceIntervalsByRoomId,
        rosterBannerNow,
      )

      return {
        assignmentGroup,
        participation,
      }
    })

    return {
      totalRooms: roomSummaries.length,
      joinedRooms: roomSummaries.filter(
        ({ participation }) => participation?.status === 'joined',
      ).length,
      partialRooms: roomSummaries.filter(
        ({ participation }) => participation?.status === 'partial',
      ).length,
      missedRooms: roomSummaries.filter(
        ({ participation }) => participation?.status === 'missed',
      ).length,
      roomSummaries,
    }
  }, [rosterAssignmentIndex?.orderedRoomGroups, rosterBannerNow, rosterPresenceIntervalsByRoomId])

  const handleViewModeChange = (nextViewMode) => {
    setViewMode(nextViewMode)
    localStorage.setItem('mentorClinicAssociationsViewMode', nextViewMode)
  }

  const [anchorEl, setAnchorEl] = useState(null)
  const [openId, setOpenId] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event, openId) => {
    setAnchorEl(event.currentTarget)
    setOpenId(openId)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Determine active tab based on current route
  const currentTab = location.pathname.includes('preferences-and-settings')
    ? 'preferences'
    : location.pathname.includes('manage-my-bookings')
    ? 'manage-bookings'
    : location.pathname.includes('stats')
    ? 'stats'
    : 'bookings'

  useEffect(() => {
    if (currentTab !== 'bookings' || !primaryRosterGroup) {
      return undefined
    }

    setRosterBannerNow(dayjs())

    const intervalId = setInterval(() => {
      setRosterBannerNow(dayjs())
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [currentTab, primaryRosterGroup])

  const handleTabChange = (event, newValue) => {
    if (newValue === 'bookings') {
      navigate('/mentor-home/my-clinic-associations')
    } else if (newValue === 'preferences') {
      navigate('/mentor-home/preferences-and-settings')
    } else if (newValue === 'stats') {
      navigate('/mentor-home/stats')
    } else if (newValue === 'manage-bookings') {
      navigate('/mentor-home/manage-my-bookings')
    }
  }

  const fetchLegacyOverview = useCallback(async () => {
    if (!mentorProfileData?.id) return []

    try {
      const [roomsResponse, auditResponse, bookingsResponse] = await Promise.all([
        getMPDataWithoutParams('/api/secure/teacher/associated-chamber-rooms/all'),
        getMPDataWithoutParams('/api/secure/mentor/chamber-rooms/status/audit'),
        getMPDataWithoutParams(
          `${buildMyClinicAssociationsApiUrl(
            '/api/secure/teacher/booked-slots/all-with-patient-info',
          )}&get-patient-details=false`,
        ),
      ])

      const chamberRooms = isCommonResponseSuccessfulV2(roomsResponse)
        ? getDataMapFromCommonResponse(roomsResponse)?.chamberRooms || []
        : []
      const auditStatuses = isCommonResponseSuccessfulV2(auditResponse)
        ? getDataMapFromCommonResponse(auditResponse)?.chamberRoomAuditStatus || []
        : []
      const bookedEvents = isCommonResponseSuccessfulV2(bookingsResponse)
        ? getDataMapFromCommonResponse(bookingsResponse)?.bookedEvents || []
        : []

      const auditStatusByRoomId = auditStatuses.reduce((accumulator, statusItem) => {
        accumulator[statusItem?.chamberRoomId] = statusItem
        return accumulator
      }, {})

      const clinicChamberByLocationId = (mentorClinicChambersData || []).reduce(
        (accumulator, chamberItem) => {
          if (chamberItem?.clinicId && !accumulator[chamberItem.clinicId]) {
            accumulator[chamberItem.clinicId] = chamberItem
          }
          return accumulator
        },
        {},
      )

      const bookingSummaryByLocationKey = (mentorClinicAssociationDataFromContext || []).reduce(
        (accumulator, locationItem) => {
          const key = `${locationItem?.clinicBrandId}-${locationItem?.id}`
          const locationBookedEvents = bookedEvents
            .filter(
              (eventItem) =>
                eventItem?.clinicId === locationItem?.id &&
                eventItem?.clinicBrandId === locationItem?.clinicBrandId &&
                eventItem?.mentorId === mentorProfileData?.id &&
                eventItem?.status !== 'cancelled',
            )
            .sort((a, b) => dayjs(a?.startTime).diff(dayjs(b?.startTime)))

          const waitingEvents = locationBookedEvents.filter((eventItem) => !eventItem?.markedCompleted)
          accumulator[key] = {
            clinicLocationId: locationItem?.id,
            clinicBrandId: locationItem?.clinicBrandId,
            totalBookings: locationBookedEvents.length,
            waitingCount: waitingEvents.length,
            earliestTime: waitingEvents?.[0]?.startTime || null,
          }
          return accumulator
        },
        {},
      )

      const fallbackLocations = (mentorClinicAssociationDataFromContext || []).map((locationItem) => {
        const roomList = chamberRooms
          .filter((roomItem) => roomItem?.clinicLocationId === locationItem?.id)
          .map((roomItem) => {
            const auditStatus = auditStatusByRoomId?.[roomItem?.id]
            const auditorsAllowedNow = auditStatus?.auditorsAllowedNow || []
            const canVideoAudit =
              auditStatus?.auditChannelActive &&
              auditorsAllowedNow?.includes?.(mentorProfileData?.id)

            return {
              ...roomItem,
              canVideoAudit: Boolean(canVideoAudit),
              activeAuditChannelId: auditStatus?.auditChannelName || null,
            }
          })

        const clinicChamber = clinicChamberByLocationId?.[locationItem?.id]
        return {
          ...locationItem,
          hasHotline: Boolean(clinicChamber?.satelliteScreenId),
          hotlineSatelliteScreenId: clinicChamber?.satelliteScreenId || null,
          bookingSummary: bookingSummaryByLocationKey?.[
            `${locationItem?.clinicBrandId}-${locationItem?.id}`
          ] || {
            clinicLocationId: locationItem?.id,
            clinicBrandId: locationItem?.clinicBrandId,
            totalBookings: 0,
            waitingCount: 0,
            earliestTime: null,
          },
          rooms: roomList,
        }
      })

      return buildBrandsFromLocations(fallbackLocations)
    } catch (error) {
      console.error('Error building legacy mentor clinic associations overview:', error)
      return []
    }
  }, [
    mentorClinicAssociationDataFromContext,
    mentorClinicChambersData,
    mentorProfileData?.id,
  ])

  const fetchOverview = useCallback(async () => {
    if (currentTab !== 'bookings') {
      setOverviewLoading(false)
      return
    }
    if (!mentorProfileData?.id) return

    setOverviewLoading(true)
    try {
      const response = await getMPDataWithoutParams(
        buildMyClinicAssociationsApiUrl('/api/secure/teacher/my-clinic-associations/overview'),
      )

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const nextBrands = response?.data?.data?.brands || []
        const hasAnyRooms = flattenClinicLocations(nextBrands).some(
          (locationItem) => (locationItem?.rooms || []).length > 0,
        )
        if (hasAnyRooms || mentorClinicAssociationDataFromContext?.length === 0) {
          setMentorClinicOverviewBrands(nextBrands)
          setUseLegacyOverviewFallback(false)
        } else {
          const fallbackBrands = await fetchLegacyOverview()
          setMentorClinicOverviewBrands(fallbackBrands)
          setUseLegacyOverviewFallback(true)
        }
      } else {
        const fallbackBrands = await fetchLegacyOverview()
        setMentorClinicOverviewBrands(fallbackBrands)
        setUseLegacyOverviewFallback(true)
      }
    } catch (error) {
      console.error('Error fetching mentor clinic associations overview:', error)
      const fallbackBrands = await fetchLegacyOverview()
      setMentorClinicOverviewBrands(fallbackBrands)
      setUseLegacyOverviewFallback(true)
    } finally {
      setOverviewLoading(false)
      setOverviewLoaded(true)
    }
  }, [
    currentTab,
    fetchLegacyOverview,
    mentorClinicAssociationDataFromContext?.length,
    mentorProfileData?.id,
  ])

  const refreshLiveStatus = useCallback(async () => {
    if (currentTab !== 'bookings' || !mentorProfileData?.id) return

    if (useLegacyOverviewFallback) {
      const fallbackBrands = await fetchLegacyOverview()
      if (fallbackBrands?.length > 0) {
        setMentorClinicOverviewBrands(fallbackBrands)
      }
      return
    }

    try {
      const response = await getMPDataWithoutParams(
        buildMyClinicAssociationsApiUrl('/api/secure/teacher/my-clinic-associations/live-status'),
      )

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const liveStatusData = response?.data?.data || {}
        setMentorClinicOverviewBrands((previousBrands) =>
          mergeLiveStatusIntoBrands(previousBrands, liveStatusData),
        )
      }
    } catch (error) {
      console.error('Error refreshing mentor clinic associations live status:', error)
      const fallbackBrands = await fetchLegacyOverview()
      if (fallbackBrands?.length > 0) {
        setMentorClinicOverviewBrands(fallbackBrands)
        setUseLegacyOverviewFallback(true)
      }
    }
  }, [currentTab, fetchLegacyOverview, mentorProfileData?.id, useLegacyOverviewFallback])

  const refreshRosterUsageLogs = useCallback(async () => {
    if (currentTab !== 'bookings' || !mentorProfileData?.id || rosteredRoomIds.length === 0) {
      setRosterUsageLogs([])
      return
    }

    try {
      const today = dayjs().format('YYYY-MM-DD')
      const response = await getMPDataWithoutParams(
        buildMyClinicAssociationsUsageLogsApiUrl({
          fromDate: today,
          toDate: today,
          chamberRoomIds: rosteredRoomIds,
          logEventIds: [
            'log-check-in',
            'log-check-in-refresh',
            'log-check-out',
            'log-thrown-out',
          ],
        }),
      )

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        setRosterUsageLogs(response?.data?.data?.usageLogs || [])
      } else {
        setRosterUsageLogs([])
      }
    } catch (error) {
      console.error('Error fetching mentor roster usage logs:', error)
      setRosterUsageLogs([])
    }
  }, [currentTab, mentorProfileData?.id, rosteredRoomIds])

  // Single fetch for all chamber room screen statuses (API returns all rooms in one response)
  const refreshChamberStatuses = useCallback(async () => {
    if (currentTab !== 'bookings') return
    const list = await getTeacherChamberRoomScreensStatus()
    setChamberRoomScreenStatusesList(list)
  }, [currentTab])

  useEffect(() => {
    fetchOverview()
  }, [fetchOverview])

  useEffect(() => {
    refreshRosterUsageLogs()
  }, [refreshRosterUsageLogs])

  useEffect(() => {
    if (currentTab !== 'bookings' || !mentorClinicAssociationData?.length) return

    const fetchStatuses = async () => {
      if (document.hidden) return
      const list = await getTeacherChamberRoomScreensStatus()
      setChamberRoomScreenStatusesList(list)
    }

    fetchStatuses()
    const statusInterval = setInterval(fetchStatuses, 30000)
    return () => {
      clearInterval(statusInterval)
    }
  }, [currentTab, mentorClinicAssociationData?.length])

  useEffect(() => {
    if (currentTab !== 'bookings' || !mentorClinicAssociationData?.length) return

    const refreshStatuses = async () => {
      if (document.hidden) return
      await refreshLiveStatus()
    }

    refreshStatuses()
    const interval = setInterval(refreshStatuses, 30000)
    return () => clearInterval(interval)
  }, [currentTab, mentorClinicAssociationData?.length, refreshLiveStatus])

  useEffect(() => {
    if (currentTab !== 'bookings' || rosteredRoomIds.length === 0) return

    const refreshRosterLogs = async () => {
      if (document.hidden) return
      await refreshRosterUsageLogs()
    }

    refreshRosterLogs()
    const interval = setInterval(refreshRosterLogs, 30000)
    return () => clearInterval(interval)
  }, [currentTab, refreshRosterUsageLogs, rosteredRoomIds.length])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshChamberStatuses()
        refreshLiveStatus()
        refreshRosterUsageLogs()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refreshChamberStatuses, refreshLiveStatus, refreshRosterUsageLogs])

  useEffect(() => {
    if (!checkedInDetails?.chamberRoomId || !mentorClinicAssociationData?.length || !mentorProfileData?.id) {
      return
    }

    const checkedInRoom = mentorClinicAssociationData
      ?.flatMap((clinicLocation) => clinicLocation?.rooms || [])
      ?.find((room) => room?.id === checkedInDetails?.chamberRoomId)

    if (!checkedInRoom) {
      return
    }

    const isStillCheckedIn =
      checkedInRoom?.occupied && checkedInRoom?.checkedInMentorId === mentorProfileData?.id

    if (!isStillCheckedIn) {
      localStorage.removeItem('checkedInDetails')
      localStorage.removeItem('roomDataEventTypes')
      setCheckedInDetails(null)
    }
  }, [checkedInDetails?.chamberRoomId, mentorClinicAssociationData, mentorProfileData?.id])

  // Helper function to check if a clinic location is currently open
  const isClinicLocationOpen = (location) => {
    const scheduleTimezone = location?.schedules?.[0]?.timezone
      ? decodeURIComponent(location?.schedules?.[0]?.timezone)
      : '+05:30'

    const weeklyHourMap = location?.schedules?.[0]?.weeklyHourMap
    if (!weeklyHourMap) return false

    const currentDay = dayjs().format('dddd').toLowerCase()
    const todayTimings = weeklyHourMap?.[currentDay]

    if (!todayTimings || !todayTimings?.available) return false

    const slotList = todayTimings?.slotList
    if (!slotList || slotList.length === 0) return false

    const now = dayjs().utcOffset(scheduleTimezone)

    // Check all slots for today
    for (const slot of slotList) {
      const [hours, minutes] = slot?.startTime?.split(':') || [0, 0]
      const [endHours, endMinutes] = slot?.endTime?.split(':') || [0, 0]

      // Set the date to today in the clinic's timezone
      const todayStart = now.startOf('day')
      const slotStart = todayStart.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)
      const slotEnd = todayStart.hour(parseInt(endHours)).minute(parseInt(endMinutes)).second(0)

      // Check if we're currently in this slot
      if (now.isAfter(slotStart) && now.isBefore(slotEnd)) {
        return true
      }
    }

    return false
  }

  const uniqueBrands = useMemo(() => {
    const brands =
      mentorClinicOverviewBrands?.length > 0
        ? mentorClinicOverviewBrands
        : [
            ...new Map(
              mentorClinicAssociationData?.map((item) => [
                item?.clinicBrandId,
                {
                  id: item?.clinicBrandId,
                  title: item?.clinicBrandName,
                  ownerUserIds: item?.clinicBrandOwnerUserIds,
                  icon: item?.clinicBrandIconImage || item?.clinicBrandIcon || item?.icon,
                  coverIcon: item?.clinicBrandCoverIcon || item?.coverIcon,
                  coverImage: item?.clinicBrandCoverImage || item?.clinicBrandImage || item?.coverImage,
                  locations: mentorClinicAssociationData?.filter(
                    (loc) => loc?.clinicBrandId === item?.clinicBrandId,
                  ),
                },
              ]),
            ).values(),
          ]

    return brands
      .map((brand) => {
        let totalWaitingPatients = 0
        let earliestTime = null
        let hasOpenLocation = false

        ;(brand?.locations || []).forEach((location) => {
          const key = `${brand.id}-${location.id}`
          const data = waitingPatientsData[key]
          if (data) {
            totalWaitingPatients += data.count
            if (data.earliestTime) {
              if (!earliestTime || dayjs(data.earliestTime).isBefore(dayjs(earliestTime))) {
                earliestTime = data.earliestTime
              }
            }
          }
          // Check if this location is currently open
          if (isClinicLocationOpen(location)) {
            hasOpenLocation = true
          }
        })

        return {
          ...brand,
          totalWaitingPatients,
          earliestTime,
          hasOpenLocation,
        }
      })
      .sort((a, b) => {
        // First priority: Earliest upcoming patient time (nulls last)
        if (!a.earliestTime && !b.earliestTime) {
          // Second priority: Open clinics (those that are currently open)
          if (a.hasOpenLocation && !b.hasOpenLocation) return -1
          if (!a.hasOpenLocation && b.hasOpenLocation) return 1
          // If both have same open status, sort by waiting patients count
          return b.totalWaitingPatients - a.totalWaitingPatients
        }
        if (!a.earliestTime) return 1
        if (!b.earliestTime) return -1
        
        // Both have earliest times - compare them
        const timeDiff = dayjs(a.earliestTime).diff(dayjs(b.earliestTime), 'minute')
        
        // If times are the same or very close (within 5 minutes), apply second priority
        if (Math.abs(timeDiff) <= 5) {
          // Second priority: Open clinics (those that are currently open)
          if (a.hasOpenLocation && !b.hasOpenLocation) return -1
          if (!a.hasOpenLocation && b.hasOpenLocation) return 1
          // If both have same open status, maintain time order
          return timeDiff
        }
        
        // Times are significantly different, sort by earliest time
        return timeDiff
      })
  }, [mentorClinicAssociationData, mentorClinicOverviewBrands, waitingPatientsData])

  const isSingleBrand = uniqueBrands.length === 1

  function navigateToHotline(clinicLocationData) {
    if (!clinicLocationData?.id || !clinicLocationData?.hotlineSatelliteScreenId) return
    navigate(`/hotline-for-mentor/${clinicLocationData?.id}/${clinicLocationData?.hotlineSatelliteScreenId}`)
  }

  const renderRosterAssignmentSummary = () => {
    if (currentTab !== 'bookings') return null

    const { primaryGroup, totalRooms, totalAssignments, currentCount, upcomingCount } =
      rosterAssignmentIndex.summary
    const hasHeroRosterState =
      (rosterAssignmentsLoading && totalRooms === 0) || currentCount > 0 || upcomingCount > 0

    if (!hasHeroRosterState) {
      return null
    }

    const isPrimaryRosterMatched = isCheckedIntoRosterRoom(checkedInDetails, primaryGroup)
    const isPrimaryRosterPending =
      Boolean(primaryGroup) && !isPrimaryRosterMatched && primaryGroup?.status !== 'completed'
    const checkedInMethod = isPrimaryRosterMatched
      ? checkedInDetails?.checkinMethod || checkedInDetails?.checkInMethod || null
      : null
    const primaryRosterMethodId = resolveRosterCheckInMethod(
      primaryRosterContext?.roomData,
      practitionerRoleFlags,
      checkedInMethod,
    )
    const primaryRosterMethodLabel = getRosterCheckInMethodLabel(primaryRosterMethodId)
    const primaryRosterEventType =
      primaryGroup?.primaryAssignment?.rawRosterSlot?.eventType ||
      primaryGroup?.primaryAssignment?.eventType ||
      null
    const primaryRosterServiceEntry = resolvePrimaryRosterServiceEntry({
      roomData: primaryRosterContext?.roomData,
      clinicId: primaryGroup?.clinicLocationId,
      mentorEventTypes: mentorProfileData?.eventTypes || [],
      rosterEventType: primaryRosterEventType,
      roleFlags: practitionerRoleFlags,
    })
    const primaryRosterEventLabel = getRosterEventTypeLabel(
      primaryRosterEventType,
      practitionerRoleFlags,
    )
    const primaryRosterFallbackPath =
      primaryGroup?.clinicBrandId && primaryGroup?.clinicLocationId
        ? `/mentor-home/my-clinic-associations/${primaryGroup.clinicBrandId}/${primaryGroup.clinicLocationId}`
        : null
    const primaryRosterWorkspacePath =
      primaryGroup?.clinicBrandId && primaryGroup?.clinicLocationId
        ? primaryRosterMethodId === REMOTE_CHECKIN_METHOD
          ? `/mentor-home/join-chamber-room/${primaryGroup.clinicBrandId}/${primaryGroup.clinicLocationId}`
          : primaryRosterFallbackPath
        : null
    const primaryRosterActionLabel = `Check In ${primaryRosterMethodLabel} for ${primaryRosterEventLabel}`
    const primaryRosterTimingMeta = getRosterTimingMeta(
      primaryGroup,
      rosterBannerNow,
      isPrimaryRosterMatched,
    )
    const handlePrimaryRosterAction = async () => {
      if (!primaryRosterFallbackPath || primaryRosterActionLoading) {
        return
      }

      const roomData = primaryRosterContext?.roomData
      const roomOccupiedByAnotherMentor =
        roomData?.occupied &&
        roomData?.checkedInMentorId &&
        String(roomData?.checkedInMentorId || '') !== String(mentorProfileData?.id || '')

      if (roomOccupiedByAnotherMentor) {
        toast.info('This assigned chamber is currently occupied. Opening the clinic view so you can request checkout.', {
          autoClose: 3500,
        })
        navigate(primaryRosterFallbackPath)
        return
      }

      if (!roomData?.id || !primaryRosterServiceEntry?.checkInOption?.id) {
        navigate(primaryRosterFallbackPath)
        return
      }

      setPrimaryRosterActionLoading(true)

      try {
        const response = await checkIntoRoom({
          ...roomData,
          clinicBrandId: primaryGroup?.clinicBrandId,
          clinicLocationId: primaryGroup?.clinicLocationId,
          selectedCheckinEventOption: primaryRosterServiceEntry.checkInOption.id,
          checkinMethod: primaryRosterMethodId,
        })

        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const emrUsageLog = response?.data?.data?.['emr-usage-log']
          const chamberRoom = response?.data?.data?.['chamber-room']
          const updatedCheckInDetails = emrUsageLog
            ? {
                ...emrUsageLog,
                roomName: chamberRoom?.title || roomData?.title || roomData?.name,
              }
            : null

          if (updatedCheckInDetails) {
            updateCheckInWithValidation(updatedCheckInDetails)
            broadcastCheckInUpdate(updatedCheckInDetails, chamberRoom || roomData)
            setCheckedInDetails(updatedCheckInDetails)
          }

          if (chamberRoom || roomData) {
            localStorage.setItem(
              'peekedInRoomDetails',
              JSON.stringify(chamberRoom || roomData),
            )
          }

          if (primaryRosterServiceEntry?.roomEventTypes?.length > 0) {
            localStorage.setItem(
              'roomDataEventTypes',
              JSON.stringify(primaryRosterServiceEntry.roomEventTypes),
            )
          } else {
            localStorage.removeItem('roomDataEventTypes')
          }

          refreshLiveStatus()
          refreshChamberStatuses()
          refreshRosterUsageLogs()
          refreshTodayRosterAssignments?.()

          navigate(primaryRosterWorkspacePath || primaryRosterFallbackPath)
          return
        }

        const responseMessage = String(response?.data?.message || '').toLowerCase()
        if (
          responseMessage.includes('occupied') ||
          responseMessage.includes('already checked in') ||
          responseMessage.includes('room busy')
        ) {
          navigate(primaryRosterFallbackPath)
        }
      } catch (error) {
        console.error('Error checking into primary roster room:', error)
        toast.error('Could not check you in right now. Opening the clinic view instead.')
        navigate(primaryRosterFallbackPath)
      } finally {
        setPrimaryRosterActionLoading(false)
      }
    }
    const rosterCardTone = isPrimaryRosterMatched
      ? {
          border: 'rgba(16, 185, 129, 0.64)',
          ring: 'rgba(16, 185, 129, 0.2)',
          shadow: '0 26px 60px rgba(5, 150, 105, 0.16)',
          background:
            'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(255, 255, 255, 0.97) 48%, rgba(236, 253, 245, 0.98) 100%)',
        }
      : primaryRosterTimingMeta?.tone === 'late'
      ? {
          border: 'rgba(239, 68, 68, 0.72)',
          ring: 'rgba(239, 68, 68, 0.2)',
          shadow: '0 30px 68px rgba(239, 68, 68, 0.2)',
          background:
            'linear-gradient(135deg, rgba(254, 226, 226, 0.96) 0%, rgba(255, 255, 255, 0.98) 40%, rgba(255, 237, 213, 0.96) 100%)',
        }
      : primaryGroup?.status === 'upcoming'
      ? {
          border: 'rgba(249, 115, 22, 0.68)',
          ring: 'rgba(249, 115, 22, 0.18)',
          shadow: '0 28px 64px rgba(249, 115, 22, 0.18)',
          background:
            'linear-gradient(135deg, rgba(255, 237, 213, 0.98) 0%, rgba(255, 255, 255, 0.98) 44%, rgba(254, 249, 195, 0.94) 100%)',
        }
      : {
          border: 'rgba(14, 165, 233, 0.58)',
          ring: 'rgba(14, 165, 233, 0.16)',
          shadow: '0 24px 52px rgba(14, 165, 233, 0.14)',
          background:
            'linear-gradient(135deg, rgba(14, 165, 233, 0.14) 0%, rgba(124, 58, 237, 0.08) 42%, rgba(255, 255, 255, 0.98) 100%)',
        }
    const rosterTimingTone =
      primaryRosterTimingMeta?.tone === 'late'
        ? {
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.16) 0%, rgba(249, 115, 22, 0.18) 100%)',
            border: 'rgba(239, 68, 68, 0.34)',
            color: 'rgba(153, 27, 27, 0.98)',
            icon: 'rgba(220, 38, 38, 0.96)',
          }
        : primaryRosterTimingMeta?.tone === 'matched'
        ? {
            background:
              'linear-gradient(135deg, rgba(16, 185, 129, 0.14) 0%, rgba(52, 211, 153, 0.18) 100%)',
            border: 'rgba(16, 185, 129, 0.3)',
            color: 'rgba(5, 150, 105, 0.98)',
            icon: 'rgba(5, 150, 105, 0.96)',
          }
        : {
            background:
              'linear-gradient(135deg, rgba(249, 115, 22, 0.14) 0%, rgba(251, 191, 36, 0.18) 100%)',
            border: 'rgba(249, 115, 22, 0.3)',
            color: 'rgba(194, 65, 12, 0.98)',
            icon: 'rgba(234, 88, 12, 0.96)',
          }
    const primaryRosterStatusLabel = isPrimaryRosterMatched
      ? 'Checked in here'
      : primaryGroup?.status === 'completed'
      ? 'Roster completed'
      : primaryGroup?.status === 'upcoming'
      ? 'Upcoming check-in'
      : 'Check-in pending'

    if (!rosterAssignmentsLoading && totalRooms === 0) {
      return null
    }

    return (
      <Card
        sx={{
          mb: { xs: 2.5, sm: 3 },
          borderRadius: '28px',
          border: `2px solid ${rosterCardTone.border}`,
          background: rosterCardTone.background,
          boxShadow: `${rosterCardTone.shadow}, 0 0 0 6px ${rosterCardTone.ring}`,
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          {rosterAssignmentsLoading && totalRooms === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={24} sx={{ color: 'rgba(14, 165, 233, 0.9)' }} />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: 'rgba(15, 23, 42, 0.9)' }}
                >
                  Checking today&apos;s chamber roster
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(71, 85, 105, 0.82)' }}>
                  Pulling your expected clinic, location, and room assignments for today.
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      letterSpacing: '0.12em',
                      fontWeight: 800,
                      color: 'rgba(3, 105, 161, 0.92)',
                    }}
                  >
                    Today&apos;s Chamber Roster
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      mt: 0.5,
                      fontSize: { xs: '22px', sm: '26px' },
                      fontWeight: 800,
                      color: 'rgba(15, 23, 42, 0.96)',
                    }}
                  >
                    {primaryGroup?.chamberRoomTitle || 'Assigned chamber room'}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.5,
                      color: 'rgba(51, 65, 85, 0.86)',
                      lineHeight: 1.65,
                    }}
                  >
                    {primaryGroup?.clinicBrandName}
                    {primaryGroup?.clinicLocationName
                      ? ` > ${primaryGroup.clinicLocationName}`
                      : ''}
                    {primaryGroup?.status === 'current'
                      ? ` is rostered for you right now (${primaryGroup?.detailLabel}).`
                      : primaryGroup?.status === 'upcoming'
                      ? ` is your next rostered check-in (${primaryGroup?.detailLabel}).`
                      : ` was rostered for you earlier today (${primaryGroup?.detailLabel}).`}
                    {isPrimaryRosterMatched
                      ? ' You are already checked into this assigned room.'
                      : isPrimaryRosterPending
                      ? ' You still need to check in here.'
                      : ''}
                  </Typography>

                  {primaryRosterTimingMeta && (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1.1,
                        minWidth: 0,
                        maxWidth: '100%',
                        px: { xs: 1.25, sm: 1.5 },
                        py: { xs: 1, sm: 1.1 },
                        borderRadius: '18px',
                        border: `1px solid ${rosterTimingTone.border}`,
                        background: rosterTimingTone.background,
                        boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
                        '@keyframes rosterTickerPulse': {
                          '0%': {
                            transform: 'scale(1)',
                            boxShadow: `0 0 0 0 ${rosterTimingTone.border}`,
                          },
                          '70%': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 0 0 10px rgba(255, 255, 255, 0)',
                          },
                          '100%': {
                            transform: 'scale(1)',
                            boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)',
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          minWidth: 34,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.92)',
                          color: rosterTimingTone.icon,
                          animation: 'rosterTickerPulse 1.6s ease-in-out infinite',
                        }}
                      >
                        <AccessTime sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            color: rosterTimingTone.color,
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                          }}
                        >
                          {primaryRosterTimingMeta.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.25,
                            color: 'rgba(71, 85, 105, 0.86)',
                            lineHeight: 1.35,
                          }}
                        >
                          {primaryRosterTimingMeta.helperText}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {primaryGroup && (
                    <Chip
                      label={primaryRosterStatusLabel}
                      sx={{
                        backgroundColor: isPrimaryRosterMatched
                          ? 'rgba(16, 185, 129, 0.14)'
                          : isPrimaryRosterPending
                          ? 'rgba(249, 115, 22, 0.14)'
                          : 'rgba(148, 163, 184, 0.16)',
                        color: isPrimaryRosterMatched
                          ? 'rgba(5, 150, 105, 0.96)'
                          : isPrimaryRosterPending
                          ? 'rgba(194, 65, 12, 0.96)'
                          : 'rgba(71, 85, 105, 0.92)',
                        fontWeight: 700,
                        border: isPrimaryRosterMatched
                          ? '1px solid rgba(16, 185, 129, 0.16)'
                          : isPrimaryRosterPending
                          ? '1px solid rgba(249, 115, 22, 0.16)'
                          : '1px solid rgba(148, 163, 184, 0.16)',
                      }}
                    />
                  )}
                  {primaryGroup && (
                    <Chip
                      icon={<LocalHospital sx={{ fontSize: '16px !important' }} />}
                      label={`${primaryRosterEventLabel} · ${primaryRosterMethodLabel}`}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.76)',
                        color: 'rgba(30, 41, 59, 0.94)',
                        fontWeight: 700,
                        border: `1px solid ${rosterCardTone.border}`,
                      }}
                    />
                  )}
                  <Chip
                    icon={<MeetingRoom sx={{ fontSize: '16px !important' }} />}
                    label={`${totalRooms} assigned room${totalRooms !== 1 ? 's' : ''}`}
                    sx={{
                      backgroundColor: 'rgba(14, 165, 233, 0.12)',
                      color: 'rgba(3, 105, 161, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(14, 165, 233, 0.16)',
                    }}
                  />
                  <Chip
                    icon={<EventNote sx={{ fontSize: '16px !important' }} />}
                    label={`${totalAssignments} slot${totalAssignments !== 1 ? 's' : ''} today`}
                    sx={{
                      backgroundColor: 'rgba(124, 58, 237, 0.12)',
                      color: 'rgba(124, 58, 237, 0.94)',
                      fontWeight: 700,
                      border: '1px solid rgba(124, 58, 237, 0.16)',
                    }}
                  />
                  {currentCount > 0 && (
                    <Chip
                      icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                      label={`${currentCount} live now`}
                      sx={{
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        color: 'rgba(5, 150, 105, 0.94)',
                        fontWeight: 700,
                        border: '1px solid rgba(16, 185, 129, 0.16)',
                      }}
                    />
                  )}
                  {upcomingCount > 0 && (
                    <Chip
                      icon={<CalendarMonth sx={{ fontSize: '16px !important' }} />}
                      label={`${upcomingCount} upcoming`}
                      sx={{
                        backgroundColor: 'rgba(249, 115, 22, 0.12)',
                        color: 'rgba(194, 65, 12, 0.94)',
                        fontWeight: 700,
                        border: '1px solid rgba(249, 115, 22, 0.16)',
                      }}
                    />
                  )}
                </Box>
              </Box>

              {isPrimaryRosterPending && primaryGroup?.clinicBrandId && primaryGroup?.clinicLocationId && (
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handlePrimaryRosterAction}
                    disabled={!primaryRosterFallbackPath || primaryRosterActionLoading}
                    sx={{
                      borderRadius: '999px',
                      px: { xs: 2, sm: 2.5 },
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      minHeight: 48,
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: '100%',
                      boxShadow: '0 16px 32px rgba(14, 165, 233, 0.22)',
                      background:
                        'linear-gradient(135deg, rgba(14, 165, 233, 0.96) 0%, rgba(59, 130, 246, 0.92) 100%)',
                      '&:hover': {
                        boxShadow: '0 18px 36px rgba(2, 132, 199, 0.28)',
                        background:
                          'linear-gradient(135deg, rgba(2, 132, 199, 0.96) 0%, rgba(37, 99, 235, 0.92) 100%)',
                      },
                      '& .MuiButton-startIcon': {
                        mr: 1,
                      },
                    }}
                    startIcon={
                      primaryRosterActionLoading ? (
                        <CircularProgress size={18} sx={{ color: 'inherit' }} />
                      ) : (
                        <AccessTime sx={{ fontSize: 20 }} />
                      )
                    }
                  >
                    {primaryRosterActionLoading ? 'Checking you in...' : primaryRosterActionLabel}
                  </Button>
                </Box>
              )}

              <Box
                sx={{
                  mt: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(auto-fit, minmax(240px, 1fr))',
                  },
                  gap: 1.25,
                }}
              >
                {rosterAssignmentIndex.orderedRoomGroups.slice(0, 3).map((assignmentGroup) => (
                  <Box
                    key={assignmentGroup?.chamberRoomId}
                    sx={{
                      borderRadius: '22px',
                      border: '1px solid rgba(148, 163, 184, 0.18)',
                      backgroundColor: 'rgba(255, 255, 255, 0.76)',
                      px: 1.5,
                      py: 1.5,
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <Chip
                      size="small"
                      label={assignmentGroup?.statusLabel}
                      sx={{
                        mb: 1,
                        backgroundColor:
                          assignmentGroup?.status === 'current'
                            ? 'rgba(16, 185, 129, 0.14)'
                            : assignmentGroup?.status === 'upcoming'
                            ? 'rgba(14, 165, 233, 0.14)'
                            : 'rgba(148, 163, 184, 0.16)',
                        color:
                          assignmentGroup?.status === 'current'
                            ? 'rgba(5, 150, 105, 0.96)'
                            : assignmentGroup?.status === 'upcoming'
                            ? 'rgba(3, 105, 161, 0.96)'
                            : 'rgba(71, 85, 105, 0.92)',
                        fontWeight: 700,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: 'rgba(15, 23, 42, 0.94)' }}
                    >
                      {assignmentGroup?.chamberRoomTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.4, color: 'rgba(71, 85, 105, 0.86)', lineHeight: 1.55 }}
                    >
                      {assignmentGroup?.clinicBrandName}
                      {assignmentGroup?.clinicLocationName
                        ? ` > ${assignmentGroup.clinicLocationName}`
                        : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.85, fontWeight: 700, color: 'rgba(3, 105, 161, 0.94)' }}
                    >
                      {assignmentGroup?.detailLabel}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    )
  }

  const renderCompletedRosterAssignmentSummary = () => {
    if (currentTab !== 'bookings') return null

    const { totalRooms, currentCount, upcomingCount } = rosterAssignmentIndex.summary
    const hasCompletedRosterOnly =
      totalRooms > 0 && currentCount === 0 && upcomingCount === 0 && !rosterAssignmentsLoading

    if (!hasCompletedRosterOnly || completedRosterSummary.totalRooms === 0) {
      return null
    }

    const summaryTone =
      completedRosterSummary.missedRooms > 0
        ? {
            border: 'rgba(251, 146, 60, 0.24)',
            background:
              'linear-gradient(135deg, rgba(255, 247, 237, 0.96) 0%, rgba(255, 255, 255, 0.98) 52%, rgba(255, 251, 235, 0.98) 100%)',
            shadow: '0 20px 44px rgba(249, 115, 22, 0.08)',
          }
        : {
            border: 'rgba(16, 185, 129, 0.22)',
            background:
              'linear-gradient(135deg, rgba(236, 253, 245, 0.94) 0%, rgba(255, 255, 255, 0.98) 58%, rgba(240, 253, 250, 0.98) 100%)',
            shadow: '0 18px 40px rgba(16, 185, 129, 0.08)',
          }

    const roomStatusLabel = (participation) => {
      if (participation?.status === 'joined') {
        return participation?.totalAssignments > 1
          ? `Joined all ${participation.totalAssignments} slots`
          : 'Joined'
      }

      if (participation?.status === 'partial') {
        return `Joined ${participation?.joinedAssignmentCount || 0} of ${
          participation?.totalAssignments || 0
        } slots`
      }

      return 'Did not join'
    }

    const roomStatusTone = (participation) => {
      if (participation?.status === 'joined') {
        return {
          background: 'rgba(16, 185, 129, 0.14)',
          color: 'rgba(5, 150, 105, 0.96)',
          border: 'rgba(16, 185, 129, 0.18)',
        }
      }

      if (participation?.status === 'partial') {
        return {
          background: 'rgba(245, 158, 11, 0.14)',
          color: 'rgba(180, 83, 9, 0.96)',
          border: 'rgba(245, 158, 11, 0.18)',
        }
      }

      return {
        background: 'rgba(248, 113, 113, 0.14)',
        color: 'rgba(185, 28, 28, 0.96)',
        border: 'rgba(248, 113, 113, 0.18)',
      }
    }

    return (
      <Card
        sx={{
          mt: { xs: 2.5, sm: 3 },
          borderRadius: '24px',
          border: `1px solid ${summaryTone.border}`,
          background: summaryTone.background,
          boxShadow: summaryTone.shadow,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: '0.12em',
                  fontWeight: 800,
                  color: 'rgba(71, 85, 105, 0.84)',
                }}
              >
                Today&apos;s Chamber Roster Summary
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mt: 0.5,
                  fontWeight: 800,
                  color: 'rgba(15, 23, 42, 0.94)',
                }}
              >
                All rostered slots for today are over
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  color: 'rgba(71, 85, 105, 0.86)',
                  lineHeight: 1.6,
                }}
              >
                This section is now passive. It stays out of the hero position and only shows
                whether you joined each assigned chamber room today.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<MeetingRoom sx={{ fontSize: '16px !important' }} />}
                label={`${completedRosterSummary.totalRooms} room${
                  completedRosterSummary.totalRooms !== 1 ? 's' : ''
                }`}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.84)',
                  color: 'rgba(30, 41, 59, 0.94)',
                  fontWeight: 700,
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                }}
              />
              {completedRosterSummary.joinedRooms > 0 && (
                <Chip
                  label={`${completedRosterSummary.joinedRooms} joined`}
                  sx={{
                    backgroundColor: 'rgba(16, 185, 129, 0.14)',
                    color: 'rgba(5, 150, 105, 0.96)',
                    fontWeight: 700,
                    border: '1px solid rgba(16, 185, 129, 0.18)',
                  }}
                />
              )}
              {completedRosterSummary.partialRooms > 0 && (
                <Chip
                  label={`${completedRosterSummary.partialRooms} partial`}
                  sx={{
                    backgroundColor: 'rgba(245, 158, 11, 0.14)',
                    color: 'rgba(180, 83, 9, 0.96)',
                    fontWeight: 700,
                    border: '1px solid rgba(245, 158, 11, 0.18)',
                  }}
                />
              )}
              {completedRosterSummary.missedRooms > 0 && (
                <Chip
                  label={`${completedRosterSummary.missedRooms} missed`}
                  sx={{
                    backgroundColor: 'rgba(248, 113, 113, 0.14)',
                    color: 'rgba(185, 28, 28, 0.96)',
                    fontWeight: 700,
                    border: '1px solid rgba(248, 113, 113, 0.18)',
                  }}
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(auto-fit, minmax(280px, 1fr))',
              },
              gap: 1.25,
            }}
          >
            {completedRosterSummary.roomSummaries.map(({ assignmentGroup, participation }) => {
              const tone = roomStatusTone(participation)

              return (
                <Box
                  key={assignmentGroup?.chamberRoomId}
                  sx={{
                    borderRadius: '20px',
                    border: '1px solid rgba(148, 163, 184, 0.18)',
                    backgroundColor: 'rgba(255, 255, 255, 0.82)',
                    px: 1.5,
                    py: 1.4,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, color: 'rgba(15, 23, 42, 0.94)' }}
                      >
                        {assignmentGroup?.chamberRoomTitle}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.35, color: 'rgba(71, 85, 105, 0.86)', lineHeight: 1.55 }}
                      >
                        {assignmentGroup?.clinicBrandName}
                        {assignmentGroup?.clinicLocationName
                          ? ` > ${assignmentGroup.clinicLocationName}`
                          : ''}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={roomStatusLabel(participation)}
                      sx={{
                        backgroundColor: tone.background,
                        color: tone.color,
                        fontWeight: 700,
                        border: `1px solid ${tone.border}`,
                        maxWidth: { xs: '100%', sm: 'unset' },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.75,
                    }}
                  >
                    <Chip
                      size="small"
                      icon={<EventNote sx={{ fontSize: '15px !important' }} />}
                      label={assignmentGroup?.detailLabel || assignmentGroup?.timeLabel || 'Rostered today'}
                      sx={{
                        backgroundColor: 'rgba(14, 165, 233, 0.12)',
                        color: 'rgba(3, 105, 161, 0.94)',
                        fontWeight: 700,
                        border: '1px solid rgba(14, 165, 233, 0.16)',
                      }}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>
        </CardContent>
      </Card>
    )
  }

  useEffect(() => {
    if (overviewLoaded && !overviewLoading && mentorClinicAssociationData?.length === 0) {
      navigate('/mentor-home')
    }
  }, [mentorClinicAssociationData?.length, navigate, overviewLoaded, overviewLoading])

  // Render list view (replaces the legacy classic layout)
  const renderListView = () => {
    return (
      <MentorClinicAssociationsListView
        brands={uniqueBrands}
        waitingPatientsData={waitingPatientsData}
        checkedInDetails={checkedInDetails}
        isClinicOwner={getAllRolesInBoolean().isClinicOwner}
        currentUserId={mentorProfileData?.userId}
        onNavigateToHotline={navigateToHotline}
        onOpenLocationStats={(clinicLocationData) => {
          setShowClinicStats(true)
          setSelectedClinicLocationForClinicStats(clinicLocationData)
        }}
        screenStatusesList={chamberRoomScreenStatusesList}
        onRefreshChamberStatuses={refreshChamberStatuses}
        rosterAssignmentIndex={rosterAssignmentIndex}
      />
    )
  }

  // Render new version (enhanced implementation)
  const renderNewVersion = () => {
    return (
      <>
        {overviewLoading && mentorClinicOverviewBrands.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} sx={{ color: 'rgba(16, 185, 129, 0.8)' }} />
            </Box>
          ) : isSingleBrand ? (
            // Single Brand - Optimized Layout with Card
            <Box>
              {uniqueBrands.map((brand) => {
                const firstLocation = brand.locations?.[0]
                const hasMultipleLocations = brand.locations?.length > 1
                const isCheckedInBrand =
                  checkedInDetails?.clinicBrandId === brand.id

                return (
                  <ClinicBrandCard
                    key={brand.id}
                    isSingle={true}
                    sx={{
                      mb: 4,
                      ...(isCheckedInBrand && {
                        border: '2px solid rgba(116, 80, 170, 0.5)',
                        boxShadow: '0 8px 32px rgba(116, 80, 170, 0.25)',
                        background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)',
                        transform: 'scale(1.01)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 12px 40px rgba(116, 80, 170, 0.3)',
                        },
                      }),
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                  <Box
                    sx={{
                      mb: 0,
                    }}
                  >
                    {/* Brand Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, minWidth: 0 }}>
                          {(() => {
                            const brandImage = getImageLink(
                              brand.coverIcon || brand.icon,
                              brand.coverImage,
                              false,
                            )
                            return brandImage ? (
                              <Box
                                component="img"
                                src={brandImage}
                                alt={brand.title}
                                sx={{
                                  width: { xs: 48, sm: 56 },
                                  height: { xs: 48, sm: 56 },
                                  borderRadius: '14px',
                                  objectFit: 'cover',
                                  border: '1px solid rgba(116, 80, 170, 0.2)',
                                  flexShrink: 0,
                                  background: 'rgba(116, 80, 170, 0.05)',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: { xs: 48, sm: 56 },
                                  height: { xs: 48, sm: 56 },
                                  borderRadius: '14px',
                                  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
                                  border: '1px solid rgba(116, 80, 170, 0.2)',
                                  flexShrink: 0,
                                }}
                              >
                                <LocalHospital
                                  sx={{
                                    fontSize: { xs: 24, sm: 28 },
                                    color: 'rgba(116, 80, 170, 0.9)',
                                  }}
                                />
                              </Box>
                            )
                          })()}
                          <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 700,
                                color: 'rgba(15, 23, 42, 0.95)',
                                fontSize: { xs: '24px', sm: '28px', md: '32px' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                              }}
                            >
                              {brand.title}
                            </Typography>
                          </Box>
                        </Box>
                        {brand.totalWaitingPatients > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Chip
                              icon={<People sx={{ fontSize: '16px !important' }} />}
                              label={`${brand.totalWaitingPatients} patient${
                                brand.totalWaitingPatients !== 1 ? 's' : ''
                              } waiting`}
                              color="primary"
                              sx={{
                                backgroundColor: 'rgba(116, 80, 170, 0.1)',
                                color: 'rgba(116, 80, 170, 0.95)',
                                fontWeight: 600,
                                fontSize: '13px',
                              }}
                            />
                            {brand.earliestTime && (
                              <Chip
                                icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                                label={`Next: ${dayjs.utc(brand.earliestTime).local().format('h:mm A')}`}
                                sx={{
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  color: 'rgba(59, 130, 246, 0.95)',
                                  fontWeight: 500,
                                  fontSize: '12px',
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                      {getAllRolesInBoolean().isClinicOwner &&
                        brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                          <IconButton
                            onClick={(e) => {
                              handleClick(e, OPEN_IDS.location)
                            }}
                            size="small"
                            sx={{
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                              '&:hover': {
                                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                              },
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        )}
                    </Box>

                    {/* Quick Access for Single Location */}
                    {!hasMultipleLocations && firstLocation ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          gap: 3,
                          flexWrap: 'wrap',
                          position: 'relative',
                        }}
                      >
                        {(() => {
                          const key = `${brand.id}-${firstLocation.id}`
                          const locationData = waitingPatientsData[key]
                          const bookingCount = locationData?.totalBookings || 0
                          const waitingCount = locationData?.count || 0
                          const isCheckedInLocation =
                            checkedInDetails?.clinicLocationId === firstLocation.id &&
                            checkedInDetails?.clinicBrandId === brand.id
                          return (
                            <Box
                              sx={{
                                position: 'relative',
                                width: { xs: '100%', sm: '100%' },
                                maxWidth: { xs: '100%', sm: '600px' },
                                border: isCheckedInLocation
                                  ? '2px solid rgba(116, 80, 170, 0.5)'
                                  : '1.5px solid rgba(116, 80, 170, 0.18)',
                                borderRadius: { xs: '14px', sm: '16px' },
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: isCheckedInLocation
                                  ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                boxShadow: isCheckedInLocation
                                  ? '0 8px 24px rgba(116, 80, 170, 0.2), 0 2px 8px rgba(116, 80, 170, 0.1)'
                                  : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
                                ...(isCheckedInLocation && {
                                  transform: 'translateY(-2px)',
                                }),
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: isCheckedInLocation
                                    ? 'linear-gradient(90deg, rgba(116, 80, 170, 0.8) 0%, rgba(116, 80, 170, 0.4) 100%)'
                                    : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                  opacity: isCheckedInLocation ? 1 : 0,
                                  transition: 'opacity 0.3s ease',
                                },
                                '&:hover': {
                                  borderColor: isCheckedInLocation
                                    ? 'rgba(116, 80, 170, 0.65)'
                                    : 'rgba(116, 80, 170, 0.35)',
                                  boxShadow: isCheckedInLocation
                                    ? '0 12px 32px rgba(116, 80, 170, 0.25), 0 4px 12px rgba(116, 80, 170, 0.15)'
                                    : '0 4px 16px rgba(116, 80, 170, 0.12), 0 2px 6px rgba(116, 80, 170, 0.08)',
                                  transform: isCheckedInLocation ? 'translateY(-3px) scale(1.01)' : 'translateY(-2px) scale(1.005)',
                                  '&::before': {
                                    opacity: 1,
                                  },
                                },
                              }}
                            >
                              {isCheckedInLocation && (
                                <Chip
                                  icon={<MeetingRoom sx={{ fontSize: '12px !important' }} />}
                                  label="Checked In"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    left: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    backgroundColor: 'rgba(116, 80, 170, 0.95)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '9px', sm: '10px' },
                                    height: { xs: '22px', sm: '24px' },
                                    boxShadow: '0 3px 10px rgba(116, 80, 170, 0.4)',
                                    border: '1px solid rgba(116, 80, 170, 0.3)',
                                    '& .MuiChip-icon': {
                                      marginLeft: '6px',
                                      color: 'white',
                                    },
                                  }}
                                />
                              )}
                              {bookingCount > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    right: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  {waitingCount > 0 && (
                                    <Chip
                                      label={waitingCount}
                                      size="small"
                                      color="error"
                                      sx={{
                                        fontSize: { xs: '9px', sm: '10px' },
                                        fontWeight: 700,
                                        height: { xs: '20px', sm: '22px' },
                                        minWidth: { xs: '20px', sm: '22px' },
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                        animation: waitingCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                        '@keyframes pulse': {
                                          '0%, 100%': {
                                            transform: 'scale(1)',
                                          },
                                          '50%': {
                                            transform: 'scale(1.05)',
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  <Chip
                                    icon={<People sx={{ fontSize: '12px !important' }} />}
                                    label={`${bookingCount} Patient${bookingCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      fontSize: { xs: '9px', sm: '10px' },
                                      fontWeight: 600,
                                      height: { xs: '20px', sm: '22px' },
                                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                      color: 'rgba(59, 130, 246, 0.95)',
                                      border: '1px solid rgba(59, 130, 246, 0.2)',
                                      '& .MuiChip-icon': {
                                        marginLeft: '4px',
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                              <MentorAssociationClinicSelectionBox
                                clinicLocationData={firstLocation}
                                showJoinHotline={Boolean(firstLocation?.hasHotline)}
                                hotlineOnClick={navigateToHotline}
                                showCheckInPerson
                                showViewStatsButton={
                                  getAllRolesInBoolean().isClinicOwner &&
                                  brand.ownerUserIds?.includes(mentorProfileData?.userId)
                                }
                                onClickViewStatsButton={(clinicLocationId) => {
                                  setShowClinicStats(true)
                                  setSelectedClinicLocationForClinicStats(clinicLocationId)
                                }}
                                containerCustomStyles={{
                                  maxWidth: '100%',
                                  width: '100%',
                                  padding: { xs: '12px 10px', sm: '14px 12px' },
                                  background: 'transparent',
                                  border: 'none',
                                  boxShadow: 'none',
                                }}
                                checkedInDetails={checkedInDetails}
                                chamberRooms={firstLocation?.rooms || []}
                                chamberRoomsLoading={overviewLoading}
                                screenStatusesList={chamberRoomScreenStatusesList}
                                onRefreshChamberStatuses={refreshChamberStatuses}
                              />
                            </Box>
                          )
                        })()}
                      </Box>
                    ) : (
                      // Multiple Locations in Single Brand
                      <ClinicChoosingContainer>
                        {brand.locations?.map((item, index) => {
                          const key = `${brand.id}-${item.id}`
                          const locationData = waitingPatientsData[key]
                          const bookingCount = locationData?.totalBookings || 0
                          const waitingCount = locationData?.count || 0
                          const isCheckedInLocation =
                            checkedInDetails?.clinicLocationId === item.id &&
                            checkedInDetails?.clinicBrandId === brand.id
                          return (
                            <Box
                              key={`location-${item?.id || index}`}
                              sx={{
                                position: 'relative',
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: { xs: '100%', sm: '400px' },
                                border: isCheckedInLocation
                                  ? '2px solid rgba(116, 80, 170, 0.5)'
                                  : '1.5px solid rgba(116, 80, 170, 0.18)',
                                borderRadius: { xs: '14px', sm: '16px' },
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: isCheckedInLocation
                                  ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                boxShadow: isCheckedInLocation
                                  ? '0 8px 24px rgba(116, 80, 170, 0.2), 0 2px 8px rgba(116, 80, 170, 0.1)'
                                  : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
                                ...(isCheckedInLocation && {
                                  transform: 'translateY(-2px)',
                                }),
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: isCheckedInLocation
                                    ? 'linear-gradient(90deg, rgba(116, 80, 170, 0.8) 0%, rgba(116, 80, 170, 0.4) 100%)'
                                    : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                  opacity: isCheckedInLocation ? 1 : 0,
                                  transition: 'opacity 0.3s ease',
                                },
                                '&:hover': {
                                  borderColor: isCheckedInLocation
                                    ? 'rgba(116, 80, 170, 0.65)'
                                    : 'rgba(116, 80, 170, 0.35)',
                                  boxShadow: isCheckedInLocation
                                    ? '0 12px 32px rgba(116, 80, 170, 0.25), 0 4px 12px rgba(116, 80, 170, 0.15)'
                                    : '0 4px 16px rgba(116, 80, 170, 0.12), 0 2px 6px rgba(116, 80, 170, 0.08)',
                                  transform: isCheckedInLocation ? 'translateY(-3px) scale(1.01)' : 'translateY(-2px) scale(1.005)',
                                  '&::before': {
                                    opacity: 1,
                                  },
                                },
                              }}
                            >
                              {/* Booking Count Badges - Enhanced */}
                              {bookingCount > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    right: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  {waitingCount > 0 && (
                                    <Chip
                                      label={waitingCount}
                                      size="small"
                                      color="error"
                                      sx={{
                                        fontSize: { xs: '9px', sm: '10px' },
                                        fontWeight: 700,
                                        height: { xs: '20px', sm: '22px' },
                                        minWidth: { xs: '20px', sm: '22px' },
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                        animation: waitingCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                        '@keyframes pulse': {
                                          '0%, 100%': {
                                            transform: 'scale(1)',
                                          },
                                          '50%': {
                                            transform: 'scale(1.05)',
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  <Chip
                                    icon={<People sx={{ fontSize: '12px !important' }} />}
                                    label={`${bookingCount} Patient${bookingCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      fontSize: { xs: '9px', sm: '10px' },
                                      fontWeight: 600,
                                      height: { xs: '20px', sm: '22px' },
                                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                      color: 'rgba(59, 130, 246, 0.95)',
                                      border: '1px solid rgba(59, 130, 246, 0.2)',
                                      '& .MuiChip-icon': {
                                        marginLeft: '4px',
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                              {isCheckedInLocation && (
                                <Chip
                                  icon={<MeetingRoom sx={{ fontSize: '12px !important' }} />}
                                  label="Checked In"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    left: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    backgroundColor: 'rgba(116, 80, 170, 0.95)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '9px', sm: '10px' },
                                    height: { xs: '22px', sm: '24px' },
                                    boxShadow: '0 3px 10px rgba(116, 80, 170, 0.4)',
                                    border: '1px solid rgba(116, 80, 170, 0.3)',
                                    '& .MuiChip-icon': {
                                      marginLeft: '6px',
                                      color: 'white',
                                    },
                                  }}
                                />
                              )}
                              <MentorAssociationClinicSelectionBox
                                clinicLocationData={item}
                                showJoinHotline={Boolean(item?.hasHotline)}
                                hotlineOnClick={navigateToHotline}
                                showCheckInPerson
                                showViewStatsButton={
                                  getAllRolesInBoolean().isClinicOwner &&
                                  brand.ownerUserIds?.includes(mentorProfileData?.userId)
                                }
                                onClickViewStatsButton={(clinicLocationId) => {
                                  setShowClinicStats(true)
                                  setSelectedClinicLocationForClinicStats(clinicLocationId)
                                }}
                                containerCustomStyles={{
                                  maxWidth: '100%',
                                  width: '100%',
                                  padding: { xs: '12px 10px', sm: '14px 12px' },
                                  background: 'transparent',
                                  border: 'none',
                                  boxShadow: 'none',
                                }}
                                checkedInDetails={checkedInDetails}
                                chamberRooms={item?.rooms || []}
                                chamberRoomsLoading={overviewLoading}
                                screenStatusesList={chamberRoomScreenStatusesList}
                                onRefreshChamberStatuses={refreshChamberStatuses}
                              />
                            </Box>
                          )
                        })}
                      </ClinicChoosingContainer>
                    )}

                    {/* Location Menu */}
                    {getAllRolesInBoolean().isClinicOwner &&
                      brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                        <Menu
                          anchorEl={anchorEl}
                          id="account-menu"
                          open={open && openId === OPEN_IDS.location}
                          onClose={handleClose}
                          onClick={handleClose}
                          PaperProps={{
                            elevation: 0,
                            sx: {
                              overflow: 'visible',
                              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                              mt: 1.5,
                              borderRadius: '12px',
                              '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                              },
                            },
                          }}
                          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                          <MenuItem
                            onClick={() => {
                              navigate('/aumhum-for-clinics')
                              handleClose()
                            }}
                            sx={{
                              fontSize: '14px',
                              '&:hover': {
                                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                              },
                            }}
                          >
                            Create new Clinic Location
                          </MenuItem>
                        </Menu>
                      )}
                    </Box>
                  </CardContent>
                </ClinicBrandCard>
                )
              })}
            </Box>
          ) : (
            // Multiple Brands - Card Grid Layout
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr', // Single column on mobile for better usability
                  sm: 'repeat(2, 1fr)', // 2 columns on small tablets
                  md: 'repeat(3, 1fr)', // 3 columns on larger screens
                },
                gap: { xs: 2.5, sm: 3 }, // Slightly increased gap on mobile
                mb: 4,
              }}
            >
              {uniqueBrands.map((brand) => {
                const isCheckedInBrand =
                  checkedInDetails?.clinicBrandId === brand.id
                return (
                  <ClinicBrandCard
                    key={brand.id}
                    isSingle={false}
                    sx={{
                      ...(isCheckedInBrand && {
                        border: '2px solid rgba(116, 80, 170, 0.5)',
                        boxShadow: '0 8px 32px rgba(116, 80, 170, 0.25)',
                        background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)',
                        transform: 'scale(1.02)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.03)',
                          boxShadow: '0 12px 40px rgba(116, 80, 170, 0.3)',
                        },
                      }),
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                      {/* Brand Header - Modernized */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2.5,
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          {/* Brand Name with Icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, minWidth: 0 }}>
                            {(() => {
                              const brandImage = getImageLink(
                                brand.coverIcon || brand.icon,
                                brand.coverImage,
                                false,
                              )
                              return brandImage ? (
                                <Box
                                  component="img"
                                  src={brandImage}
                                  alt={brand.title}
                                  sx={{
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: '12px',
                                    objectFit: 'cover',
                                    border: '1px solid rgba(116, 80, 170, 0.2)',
                                    flexShrink: 0,
                                    background: 'rgba(116, 80, 170, 0.05)',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
                                    border: '1px solid rgba(116, 80, 170, 0.2)',
                                    flexShrink: 0,
                                  }}
                                >
                                  <LocalHospital
                                    sx={{
                                      fontSize: { xs: 20, sm: 24 },
                                      color: 'rgba(116, 80, 170, 0.9)',
                                    }}
                                  />
                                </Box>
                              )
                            })()}
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: 'rgba(15, 23, 42, 0.95)',
                                  fontSize: { xs: '18px', sm: '22px', md: '24px' },
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.2,
                                }}
                              >
                                {brand.title}
                              </Typography>
                              {brand.locations?.length > 0 && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'rgba(100, 116, 139, 0.7)',
                                    fontSize: { xs: '11px', sm: '12px' },
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <LocationOn sx={{ fontSize: '14px' }} />
                                  {brand.locations.length} location{brand.locations.length !== 1 ? 's' : ''}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Stats Row - Modernized */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            {brand.totalWaitingPatients > 0 && (
                              <Chip
                                icon={<People sx={{ fontSize: '14px !important' }} />}
                                label={`${brand.totalWaitingPatients} waiting`}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(116, 80, 170, 0.12)',
                                  color: 'rgba(116, 80, 170, 0.95)',
                                  fontWeight: 600,
                                  fontSize: { xs: '10px', sm: '11px' },
                                  height: { xs: '24px', sm: '26px' },
                                  border: '1px solid rgba(116, 80, 170, 0.2)',
                                  '& .MuiChip-icon': {
                                    marginLeft: '8px',
                                  },
                                }}
                              />
                            )}
                            {brand.earliestTime && (
                              <Chip
                                icon={<AccessTime sx={{ fontSize: '14px !important' }} />}
                                label={`Next: ${dayjs.utc(brand.earliestTime).local().format('h:mm A')}`}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  color: 'rgba(59, 130, 246, 0.95)',
                                  fontWeight: 600,
                                  fontSize: { xs: '10px', sm: '11px' },
                                  height: { xs: '24px', sm: '26px' },
                                  border: '1px solid rgba(59, 130, 246, 0.2)',
                                  '& .MuiChip-icon': {
                                    marginLeft: '8px',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                        {getAllRolesInBoolean().isClinicOwner &&
                          brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClick(e, `${OPEN_IDS.location}-${brand.id}`)
                              }}
                              size="small"
                              sx={{
                                ml: 1,
                                border: '1px solid rgba(116, 80, 170, 0.2)',
                                borderRadius: '10px',
                                '&:hover': {
                                  backgroundColor: 'rgba(116, 80, 170, 0.08)',
                                  borderColor: 'rgba(116, 80, 170, 0.3)',
                                },
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                      </Box>

                      {/* Show All Locations Directly - Modernized */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: { xs: 1.5, sm: 2 },
                          mt: { xs: 2, sm: 2.5 },
                          pt: { xs: 2, sm: 2.5 },
                          borderTop: '1px solid rgba(116, 80, 170, 0.1)',
                        }}
                      >
                        {brand.locations?.map((location, locIndex) => {
                          const key = `${brand.id}-${location.id}`
                          const locationData = waitingPatientsData[key]
                          const bookingCount = locationData?.totalBookings || 0
                          const waitingCount = locationData?.count || 0
                          const isCheckedInLocation =
                            checkedInDetails?.clinicLocationId === location.id &&
                            checkedInDetails?.clinicBrandId === brand.id

                          return (
                            <Box
                              key={locIndex}
                              sx={{
                                position: 'relative',
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: { xs: '100%', sm: 'none' },
                                border: isCheckedInLocation
                                  ? '2px solid rgba(116, 80, 170, 0.5)'
                                  : '1.5px solid rgba(116, 80, 170, 0.18)',
                                borderRadius: { xs: '14px', sm: '16px' },
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: isCheckedInLocation
                                  ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                boxShadow: isCheckedInLocation
                                  ? '0 8px 24px rgba(116, 80, 170, 0.2), 0 2px 8px rgba(116, 80, 170, 0.1)'
                                  : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
                                ...(isCheckedInLocation && {
                                  transform: 'translateY(-2px)',
                                }),
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: isCheckedInLocation
                                    ? 'linear-gradient(90deg, rgba(116, 80, 170, 0.8) 0%, rgba(116, 80, 170, 0.4) 100%)'
                                    : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                  opacity: isCheckedInLocation ? 1 : 0,
                                  transition: 'opacity 0.3s ease',
                                },
                                '&:hover': {
                                  borderColor: isCheckedInLocation
                                    ? 'rgba(116, 80, 170, 0.65)'
                                    : 'rgba(116, 80, 170, 0.35)',
                                  boxShadow: isCheckedInLocation
                                    ? '0 12px 32px rgba(116, 80, 170, 0.25), 0 4px 12px rgba(116, 80, 170, 0.15)'
                                    : '0 4px 16px rgba(116, 80, 170, 0.12), 0 2px 6px rgba(116, 80, 170, 0.08)',
                                  transform: isCheckedInLocation ? 'translateY(-3px) scale(1.01)' : 'translateY(-2px) scale(1.005)',
                                  '&::before': {
                                    opacity: 1,
                                  },
                                },
                              }}
                            >
                              {/* Booking Count Badge - Enhanced */}
                              {bookingCount > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    right: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  {waitingCount > 0 && (
                                    <Chip
                                      label={waitingCount}
                                      size="small"
                                      color="error"
                                      sx={{
                                        fontSize: { xs: '9px', sm: '10px' },
                                        fontWeight: 700,
                                        height: { xs: '20px', sm: '22px' },
                                        minWidth: { xs: '20px', sm: '22px' },
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                        animation: waitingCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                        '@keyframes pulse': {
                                          '0%, 100%': {
                                            transform: 'scale(1)',
                                          },
                                          '50%': {
                                            transform: 'scale(1.05)',
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  <Chip
                                    icon={<People sx={{ fontSize: '12px !important' }} />}
                                    label={`${bookingCount} Patient${bookingCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      fontSize: { xs: '9px', sm: '10px' },
                                      fontWeight: 600,
                                      height: { xs: '20px', sm: '22px' },
                                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                      color: 'rgba(59, 130, 246, 0.95)',
                                      border: '1px solid rgba(59, 130, 246, 0.2)',
                                      '& .MuiChip-icon': {
                                        marginLeft: '4px',
                                      },
                                    }}
                                  />
                                </Box>
                              )}

                              {isCheckedInLocation && (
                                <Chip
                                  icon={<MeetingRoom sx={{ fontSize: '12px !important' }} />}
                                  label="Checked In"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    left: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    backgroundColor: 'rgba(116, 80, 170, 0.95)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '9px', sm: '10px' },
                                    height: { xs: '22px', sm: '24px' },
                                    boxShadow: '0 3px 10px rgba(116, 80, 170, 0.4)',
                                    border: '1px solid rgba(116, 80, 170, 0.3)',
                                    '& .MuiChip-icon': {
                                      marginLeft: '6px',
                                      color: 'white',
                                    },
                                  }}
                                />
                              )}
                              <MentorAssociationClinicSelectionBox
                                clinicLocationData={location}
                                showJoinHotline={Boolean(location?.hasHotline)}
                                hotlineOnClick={navigateToHotline}
                                showCheckInPerson
                                showViewStatsButton={false}
                                hideClinicRelatedInfo={false}
                                containerCustomStyles={{
                                  maxWidth: '100%',
                                  width: '100%',
                                  padding: { xs: '12px 10px', sm: '14px 12px' },
                                  background: 'transparent',
                                  border: 'none',
                                  boxShadow: 'none',
                                }}
                                checkedInDetails={checkedInDetails}
                                chamberRooms={location?.rooms || []}
                                chamberRoomsLoading={overviewLoading}
                                screenStatusesList={chamberRoomScreenStatusesList}
                                onRefreshChamberStatuses={refreshChamberStatuses}
                              />
                            </Box>
                          )
                        })}
                      </Box>
                    </CardContent>
                  </ClinicBrandCard>
                )
              })}
            </Box>
          )}

        {/* Location Menu for Multiple Brands */}
        {!isSingleBrand &&
          uniqueBrands.map((brand) => (
            <Menu
              key={`menu-${brand.id}`}
              anchorEl={anchorEl}
              id={`account-menu-${brand.id}`}
              open={open && openId === `${OPEN_IDS.location}-${brand.id}`}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  borderRadius: '12px',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                onClick={() => {
                  navigate('/aumhum-for-clinics')
                  handleClose()
                }}
                sx={{
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  },
                }}
              >
                Create new Clinic Location
              </MenuItem>
            </Menu>
          ))}

        <ClinicLocationStatsDrawer
          open={showClinicStats}
          onClose={() => setShowClinicStats(false)}
          clinicLocationData={selectedClinicLocationForClinicStats}
        />
      </>
    )
  }

  return (
    <>
      {/* Title and Profile/Portal Switcher Row */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1001,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          className: 'hide-on-print-only',
        }}
      >
        <HeaderContainer className="hide-on-print-only">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              width: '100%',
              py: 1.5,
              gap: 2,
            }}
          >
            {/* Left Column - Title */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
              <HeaderGreeting
                main
                sx={{
                  fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
                  fontWeight: 600,
                }}
              >
                {currentTab === 'bookings'
                  ? 'Clinic Check-In & Bookings'
                  : currentTab === 'stats'
                  ? 'Stats'
                  : currentTab === 'manage-bookings'
                  ? 'Manage Bookings'
                  : 'Preferences & Settings'}
              </HeaderGreeting>
              {currentTab === 'bookings' && (
                <GenericDisplayFlex sx={{ alignItems: 'center', gap: isMobile ? 0.5 : 1, mt: isMobile ? 0.25 : 0.5 }}>
                  <CalendarMonth
                    sx={{ fontSize: isMobile ? 12 : isTablet ? 14 : 16, color: 'rgba(100, 116, 139, 0.8)' }}
                  />
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      color: 'rgba(100, 116, 139, 0.8)',
                      fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
                      fontWeight: 500,
                    }}
                  >
                    {isMobile
                      ? dayjs().format('MMM DD, YYYY')
                      : dayjs().format(MOMENT_FORMATS.completeDateDetailsWithMonthShortFormAndDay)}
                  </Typography>
                </GenericDisplayFlex>
              )}
            </Box>
            {/* Right Column - Buttons (only as wide as needed) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 1 : 1.5,
                flexShrink: 0,
                justifyContent: 'flex-end',
              }}
            >
              <PortalSwitcherButton />
              {isMobile ? (
                <IconButton
                  onClick={(e) => handleClick(e, OPEN_IDS.profile)}
                  size="small"
                  sx={{
                    padding: '4px',
                    border: '2px solid rgba(16, 185, 129, 0.2)',
                    backgroundColor: 'rgba(16, 185, 129, 0.04)',
                  }}
                >
                  <Avatar
                    src={getImageLink(mentorProfileData?.profilePicId)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: '2px solid rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </IconButton>
              ) : (
                <Tooltip title="Account Menu" arrow>
                  <IconButton
                    onClick={(e) => handleClick(e, OPEN_IDS.profile)}
                    size="small"
                    sx={{
                      padding: '6px',
                      border: '2px solid rgba(16, 185, 129, 0.2)',
                      backgroundColor: 'rgba(16, 185, 129, 0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        borderColor: 'rgba(16, 185, 129, 0.4)',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <Avatar
                      src={getImageLink(mentorProfileData?.profilePicId)}
                      sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
                      }}
                    >
                      {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </HeaderContainer>
      </Box>

      {/* Tabs Row - Full Width with Proper Spacing */}
      <Box
        sx={{
          width: '100%',
          position: 'sticky',
          top: isMobile ? '80px' : '88px',
          zIndex: 1000,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          className: 'hide-on-print-only',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MentorTabs value={currentTab} onChange={handleTabChange} isMobile={isMobile}>
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="bookings"
              icon={isMobile ? <CalendarMonth /> : undefined}
              label={isMobile ? 'Check-In' : isTablet ? 'Check-In' : 'Clinic Check-In & Bookings'}
            />
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="manage-bookings"
              icon={isMobile ? <EventNote /> : undefined}
              label={isMobile ? 'Bookings' : isTablet ? 'Bookings' : 'Manage Bookings'}
            />
            {getAllRolesInBoolean().isClinicOwner && (
              <MentorTab
                isMobile={isMobile}
                iconOnly={isMobile}
                value="stats"
                icon={isMobile ? <BarChart /> : undefined}
                label={isMobile ? 'Stats' : 'Stats'}
              />
            )}
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="preferences"
              icon={isMobile ? <Settings /> : undefined}
              label={isMobile ? 'Settings' : isTablet ? 'Settings' : 'Preferences & Settings'}
            />
          </MentorTabs>
        </Box>
      </Box>

      {/* Profile Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open && openId === OPEN_IDS.profile}
        onClose={handleClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 240,
            borderRadius: '12px',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              borderLeft: '1px solid rgba(16, 185, 129, 0.1)',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            backgroundColor: 'rgba(16, 185, 129, 0.02)',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'rgba(15, 23, 42, 0.95)',
              fontSize: '14px',
              mb: 0.5,
            }}
          >
            {mentorProfileData?.name || 'Doctor'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(100, 116, 139, 0.8)',
              fontSize: '12px',
            }}
          >
            Mentor Portal
          </Typography>
        </Box>

        <MenuItem
          onClick={() => {
            navigate('/mentor-home/profile')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(71, 85, 105, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(16, 185, 129, 0.08)',
              color: 'rgba(16, 185, 129, 0.95)',
            },
          }}
        >
          <Person sx={{ fontSize: 18, mr: 1.5, color: 'rgba(16, 185, 129, 0.7)' }} />
          Profile Settings
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => {
            logoutHandlerMentor()
            navigate('/')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(239, 68, 68, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              color: 'rgba(239, 68, 68, 1)',
            },
          }}
        >
          <ExitToApp sx={{ fontSize: 18, mr: 1.5 }} />
          Sign Out
        </MenuItem>
      </Menu>

      {/* Checked-In Status Strip - Modernized with Glass Effect */}
      {checkedInDetails && (
        <Box
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(116, 80, 170, 0.2)',
            boxShadow: '0 4px 20px rgba(116, 80, 170, 0.1)',
            py: 2.5,
            px: { xs: 2, sm: 3 },
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <Box
            sx={{
              maxWidth: '1400px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.2) 0%, rgba(116, 80, 170, 0.1) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(116, 80, 170, 0.3)',
                  boxShadow: '0 4px 12px rgba(116, 80, 170, 0.15)',
                  flexShrink: 0,
                }}
              >
                <MeetingRoom
                  sx={{
                    fontSize: { xs: 24, sm: 28 },
                    color: 'rgba(116, 80, 170, 1)',
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'rgba(15, 23, 42, 0.95)',
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Checked In: {checkedInDetails?.roomName || 'Room'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {(() => {
                    const clinicLocation = mentorClinicAssociationData?.find(
                      (loc) => loc.id === checkedInDetails?.clinicLocationId,
                    )
                    const clinicBrand = uniqueBrands.find(
                      (brand) => brand.id === checkedInDetails?.clinicBrandId,
                    )
                    return (
                      <>
                        {clinicBrand && (
                          <Chip
                            label={clinicBrand.title}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(116, 80, 170, 0.15)',
                              color: 'rgba(116, 80, 170, 0.95)',
                              fontWeight: 600,
                              fontSize: '11px',
                              height: '22px',
                              border: '1px solid rgba(116, 80, 170, 0.3)',
                            }}
                          />
                        )}
                        {clinicLocation && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(100, 116, 139, 0.9)',
                              fontSize: { xs: '12px', sm: '13px' },
                              fontWeight: 500,
                            }}
                          >
                            {clinicLocation.name}
                          </Typography>
                        )}
                        {checkedInDetails?.createdTime && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(100, 116, 139, 0.7)',
                              fontSize: { xs: '11px', sm: '12px' },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <AccessTime sx={{ fontSize: '14px' }} />
                            {dayjs.utc(checkedInDetails.createdTime).local().format('h:mm A')}
                          </Typography>
                        )}
                      </>
                    )
                  })()}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: { xs: 2.5, sm: 3 },
                  py: 1,
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '14px' },
                  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.95) 0%, rgba(116, 80, 170, 0.85) 100%)',
                  boxShadow: '0 4px 12px rgba(116, 80, 170, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(116, 80, 170, 1) 0%, rgba(116, 80, 170, 0.9) 100%)',
                    boxShadow: '0 6px 16px rgba(116, 80, 170, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
                onClick={() => {
                  if (checkedInDetails?.clinicBrandId && checkedInDetails?.clinicLocationId) {
                    navigate(
                      `/mentor-home/my-clinic-associations/${checkedInDetails.clinicBrandId}/${checkedInDetails.clinicLocationId}`,
                    )
                  }
                }}
              >
                Proceed
              </Button>
              <CheckoutButtonComponent
                buttonColor="error"
                styleProp={{
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: { xs: 2.5, sm: 3 },
                  py: 1,
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '14px' },
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                }}
                additionalFunctionAfterAction={() => {
                  setCheckedInDetails(null)
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <AvailabilityPageContainer>
        {/* Action Bar */}
        <Box
          sx={{
          width: 'calc(100% - 40px)',
            maxWidth: '1400px',
            margin: '0 auto 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            padding: { xs: '0 16px', sm: '0 24px' },
            flexWrap: 'wrap',
          }}
        >
          {/* View selector for clinic operations */}
          {currentTab === 'bookings' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'rgba(71, 85, 105, 0.8)',
                }}
              >
                View
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: '999px',
                  backgroundColor: 'rgba(116, 80, 170, 0.06)',
                  border: '1px solid rgba(116, 80, 170, 0.12)',
                }}
              >
                <Button
                  size="small"
                  startIcon={<ViewAgenda sx={{ fontSize: 18 }} />}
                  variant={viewMode === VIEW_MODES.list ? 'contained' : 'text'}
                  onClick={() => handleViewModeChange(VIEW_MODES.list)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    minHeight: 34,
                    px: 1.5,
                    color:
                      viewMode === VIEW_MODES.list ? 'white' : 'rgba(71, 85, 105, 0.88)',
                    backgroundColor:
                      viewMode === VIEW_MODES.list ? 'rgba(116, 80, 170, 0.95)' : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        viewMode === VIEW_MODES.list
                          ? 'rgba(116, 80, 170, 0.88)'
                          : 'rgba(116, 80, 170, 0.08)',
                    },
                  }}
                >
                  List View
                </Button>
                <Button
                  size="small"
                  startIcon={<Dashboard sx={{ fontSize: 18 }} />}
                  variant={viewMode === VIEW_MODES.cards ? 'contained' : 'text'}
                  onClick={() => handleViewModeChange(VIEW_MODES.cards)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    minHeight: 34,
                    px: 1.5,
                    color:
                      viewMode === VIEW_MODES.cards ? 'white' : 'rgba(71, 85, 105, 0.88)',
                    backgroundColor:
                      viewMode === VIEW_MODES.cards ? 'rgba(16, 185, 129, 0.95)' : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        viewMode === VIEW_MODES.cards
                          ? 'rgba(16, 185, 129, 0.88)'
                          : 'rgba(16, 185, 129, 0.08)',
                    },
                  }}
                >
                  Card View
                </Button>
              </Box>
            </Box>
          )}

          {/* 3 Dots Menu - Hide on manage-bookings page */}
          {currentTab !== 'manage-bookings' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!checkedInDetails && <CheckoutButtonComponent />}
              <IconButton
                onClick={(e) => {
                  handleClick(e, OPEN_IDS.brand)
                }}
                size="small"
                sx={{
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          )}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open && openId === OPEN_IDS.brand}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
                borderRadius: '12px',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            onClick={() => {
              navigate('/clinic-home')
                handleClose()
              }}
              sx={{
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.08)',
                },
              }}
            >
              {getAllRolesInBoolean().isClinicOwner ? 'Add another ' : 'Create your own'} clinic
              brand +
                  </MenuItem>
                </Menu>
        </Box>

        {/* Main Content - Conditional Rendering */}
        {currentTab === 'manage-bookings' ? (
          <Box
            sx={{
              width: 'calc(100% - 40px)',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: { xs: '0 16px', sm: '0 24px' },
            }}
          >
            {/* Sub-tabs for manage-bookings */}
            <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              <MentorTabs
                value={manageBookingsSubTab}
                onChange={(e, newValue) => setManageBookingsSubTab(newValue)}
                isMobile={isMobile}
              >
                <MentorTab label="All Booked" isMobile={isMobile} />
                <MentorTab label="In Clinic (Booked)" isMobile={isMobile} />
                <MentorTab label="Video Call (Booked)" isMobile={isMobile} />
                <MentorTab label="Booking Not Confirmed" isMobile={isMobile} />
              </MentorTabs>
            </Box>
            <MentorPastEventsListing
              key={`manage-bookings-${manageBookingsSubTab}`}
              mentorProfileData={mentorProfileData}
              logsApiEndpoint="/api/secure/teacher/booked-event-info/logs/get"
              meetingSrcFilter={
                manageBookingsSubTab === 1
                  ? SESSION_PLATFORM_OFFLINE
                  : manageBookingsSubTab === 2
                  ? SESSION_PLATFORM_ONLINE
                  : undefined
              }
              filterByBookedStatus={
                manageBookingsSubTab === 3 ? false : true // Tab 3 (Not Confirmed) excludes booked, others include only booked
              }
              showOnlyUnconfirmed={manageBookingsSubTab === 3}
              hideSessionTypeFilter={true}
            />
          </Box>
        ) : currentTab === 'stats' && getAllRolesInBoolean().isClinicOwner ? (
          <Box
            sx={{
              width: 'calc(100% - 40px)',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: { xs: '0 16px', sm: '0 24px' },
            }}
          >
            <ClinicReportsWithVisualizations />
          </Box>
        ) : viewMode === VIEW_MODES.cards ? (
          <Box
            sx={{
              width: 'calc(100% - 40px)',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: { xs: '0 16px', sm: '0 24px' },
            }}
          >
            {renderRosterAssignmentSummary()}
            {overviewLoading && mentorClinicOverviewBrands.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={40} sx={{ color: 'rgba(16, 185, 129, 0.8)' }} />
              </Box>
            ) : (
              renderNewVersion()
            )}
            {renderCompletedRosterAssignmentSummary()}
          </Box>
        ) : (
          <Box
            sx={{
              width: 'calc(100% - 40px)',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: { xs: '0 16px', sm: '0 24px' },
            }}
          >
            {renderRosterAssignmentSummary()}
            {overviewLoading && mentorClinicOverviewBrands.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={40} sx={{ color: 'rgba(16, 185, 129, 0.8)' }} />
              </Box>
            ) : (
              renderListView()
            )}
            {renderCompletedRosterAssignmentSummary()}
          </Box>
        )}

      <ClinicLocationStatsDrawer
        open={showClinicStats}
        onClose={() => setShowClinicStats(false)}
        clinicLocationData={selectedClinicLocationForClinicStats}
      />
      </AvailabilityPageContainer>
    </>
  )
}
